import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Slider,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  InputAdornment,
  Select,
  MenuItem,
  IconButton,
  Avatar,
  Checkbox,
  List,
  ListItemButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Tabs,
  Tab,
  Stack,
  Divider,
  ListItem,
  Alert,
} from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import NotchTabs from '../components/common/NotchTabs';
import {
  TrendingUp,
  Tune,
  Event,
  CompareArrows,
  ShowChart,
  Science,
  Settings,
  History,
  Refresh,
  Send,
  Close,
  Add,
  Edit,
  Delete,
  Star,
  Hotel,
  TrendingDown,
  Remove,
  Map as MapIcon,
  ShowChart as RateIcon,
  MonetizationOn,
  Apartment,
  Sync,
  ArrowDropDown as ArrowDropDownIcon,
  TravelExplore,
  InfoOutlined,
  ScheduleOutlined,
  SpaceDashboard,
} from '@mui/icons-material';
import moment from 'moment';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RcTooltip,
} from 'recharts';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { getHotelTodayDateString } from '../utils/dateUtils';
import { runCompetitorRowSync } from '../utils/competitorSyncUi';
import DashboardHeader from '../components/common/DashboardHeader';
import PageToastStack from '../components/common/PageToastStack';
import { DataTable } from '../shared';
import {
  getPricingRecommendations,
  applyPricingRates,
  getRatePlans,
  getPricingConfig,
  updatePricingConfig,
  getPricingRules,
  createPricingRule,
  updatePricingRule,
  deletePricingRule,
  getPricingEvents,
  createPricingEvent,
  updatePricingEvent,
  deletePricingEvent,
  getPricingForecast,
  runPricingSimulation,
  autoDiscoverCompetitors,
  getCompetitorHotels,
  getCompetitorHotelsList,
  createCompetitorHotel,
  syncCompetitorHotel,
  syncCompetitorHotelRateOnly,
  deleteCompetitorHotel,
  searchCompetitorByName,
  getOurHotelGoogleSnapshot,
  fetchOurHotelFromGoogle,
  syncOurHotelProfileFromPlaces,
  getPricingHistory,
  getPublishLogs,
  getConfigurationRoomTypes,
  getChannels,
} from '../services/api';
import DynamicPricingDashboard from '../components/dynamicPricing/DynamicPricingDashboard';
import DynamicPricingRecommendationsTab from '../components/dynamicPricing/DynamicPricingRecommendationsTab';
import DynamicPricingTabHeader from '../components/dynamicPricing/DynamicPricingTabHeader';
import { ConfigHelpTip } from '../components/dynamicPricing/ConfigHelpTip';
import CompetitorLocationMap from '../components/dynamicPricing/CompetitorLocationMap';
import CompetitorSyncProgressDialog from '../components/dynamicPricing/CompetitorSyncProgressDialog';
import { runSequentialCompetitorSync, formatSyncFailureHint } from '../utils/competitorSequentialSync';
import {
  DYNAMIC_PRICING_ACCENT,
  getDashboardPageShellSx,
  getDynamicPricingAccentTabsSx,
  getDynamicPricingConfigTogglePaperSx,
  getDynamicPricingGuideAvatarSx,
  getDynamicPricingGuidePanelSx,
  getDynamicPricingMetricCardSx,
  getDynamicPricingOutlinedButtonSx,
  getDynamicPricingPanelSx,
  getDynamicPricingPrimaryButtonSx,
  getDynamicPricingSectionOverlineSx,
  getDynamicPricingSidebarCardSx,
  getDynamicPricingSwitchSx,
  getDynamicPricingTableContainerSx,
  getDynamicPricingTabPanelSx,
  getDynamicPricingTabsPaperSx,
  getDynamicPricingThemeColors,
  getDynamicPricingValueTypographySx,
} from '../utils/dynamicPricingTheme';
import { isDynamicPricingTabletLayout } from '../utils/dynamicPricingLayout';
import {
  buildDynamicPricingPath,
  parseDynamicPricingPath,
} from '../utils/dynamicPricingRoutes';
import {
  BOOKING_DRAWER_ACCENT,
  getBookingDrawerFooterSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPaperSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerTheme,
  getGuestDrawerContentSx,
  getGuestDrawerFieldSx,
} from '../utils/bookingDrawerLayout';
import {
  getDrawerDatePickerSlotProps,
  getDrawerRootSx,
  getDrawerSelectMenuProps,
} from '../utils/drawerOverlayProps';

const ACCENT_COLOR = DYNAMIC_PRICING_ACCENT;

const HISTORY_LOG_PAGE_SIZE = 25;

/** Long-form tooltips for Dynamic Pricing → Config (keeps JSX readable). */
const PRICING_CONFIG_HELP = {
  rateFormulaCard:
    'This block controls how strongly each live signal influences the suggested rate before guardrails. Use Simulate after changes to preview outcomes; weights work together with the Signals toggles on the right.',
  weightsSection:
    'Each slider is how much that signal matters when blending recommendations. Values are 0–100 and do not need to add up to 100—the system scales active signals together. To fully ignore a source, turn it off under Signals below (or set its weight to 0).',
  occupancyWeight:
    'How heavily the model reacts to room occupancy for the stay date. Higher = fuller hotels push suggested rates up more strongly; lower = occupancy matters less vs other signals.',
  leadTimeWeight:
    'How much booking lead time (days from booking to check-in) moves the rate. Higher = last-minute bookings can lift prices more; lower = less sensitivity to how far out guests book.',
  velocityWeight:
    'How much booking pace (pick-up speed vs your recent history) affects price. Higher = a sudden rush or slowdown changes recommendations faster.',
  competitorWeight:
    'How much synced competitor rates (Competitor tab) pull your suggestion toward the market. Higher = stay closer to competitors; lower = rely more on internal signals. Competitors need fresh rate syncs to be useful.',
  boundsSection:
    'These multipliers cap each recommendation against that room type’s base rate: suggested price stays between base × min and base × max. Use this to avoid extremes while still letting the formula move rates.',
  minMultiplier:
    'Floor as a multiple of base rate (e.g. 0.6 means never recommend below 60% of base for that room). Prevents deep discounting beyond what you consider acceptable.',
  maxMultiplier:
    'Ceiling as a multiple of base rate (e.g. 2 means never recommend more than double base). Limits peak pricing during high demand.',
  signalsSection:
    'When off, that input is excluded from the engine—not just down-weighted. Pair with weights: e.g. disable Competitor if you have no synced data yet.',
  useOccupancy:
    'Include current and forecast occupancy in the rate calculation. Turn off if occupancy data is unreliable or you want rates driven only by other factors.',
  useLeadTime:
    'Include how far in advance the booking is made. Useful for properties where booking window strongly affects willingness to pay.',
  useVelocity:
    'Include booking momentum (faster or slower pick-up than usual). Helps react to demand surges or soft periods.',
  useCompetitor:
    'Include competitor pricing from properties you track. Requires competitors and rate syncs; if empty, consider disabling to avoid noise.',
  useForecast:
    'Include demand forecast where available. Blends expected future occupancy/demand into the suggestion.',
  useEvents:
    'Include special events and holidays from your Events tab. Helps raise or adjust rates around known high-demand dates.',
  cronInterval:
    'How often background pricing jobs run (minutes). Use 0 to disable automatic runs. Does not block manual actions elsewhere; check server/celery setup if jobs never fire.',
  saveButton:
    'Writes settings to the server for this hotel. Recommendations and automation use the new values on the next run. Use Simulate to preview impact before publishing rates.',
};

/** Tooltips + copy for Dynamic Pricing → Simulate */
const PRICING_SIMULATE_HELP = {
  scenarioSection:
    'Build a what-if window: pick dates, adjust weekend premium and last-minute discount, then run. Nothing is written to your PMS—this is a revenue estimate only.',
  simStart:
    'First night included in the simulation window. The engine evaluates stays that fall in the range from this date through the end date.',
  simEnd:
    'Last night included in the window (inclusive). Widen the range to see more dates; narrow it to focus on a short period.',
  weekendMarkup:
    'Extra percentage applied on weekend nights in the scenario (vs baseline). Higher = model assumes you charge more Fri–Sun; use 0 to turn off weekend uplift in this preview.',
  lastMinuteDiscount:
    'Discount percentage applied for bookings inside the “last-minute” window before stay (server uses 3 days for this simulation). Higher = deeper cuts for very near-term bookings in the scenario only.',
  lastMinuteWindowNote:
    'Last-minute logic in this API preview uses a fixed 3-day window before check-in.',
  runButton:
    'Calls the pricing simulation service for your hotel with the current dates and levers. Results compare estimated revenue if you kept baseline behavior vs applying the scenario.',
  resultsOverview:
    'Baseline revenue uses your current pricing rules without the scenario markup/discount. Scenario revenue applies weekend markup and last-minute discount from this screen. Delta is scenario minus baseline (positive = higher estimated revenue under the scenario).',
  baselineLine:
    'Estimated revenue across the date range using existing dynamic pricing behavior, without the extra weekend markup or last-minute discount you set above.',
  scenarioLine:
    'Estimated revenue when the weekend markup and last-minute discount from this page are applied inside the model for the same period.',
  deltaLine:
    'Scenario minus baseline. Positive means the scenario is estimated to earn more; negative means less—use it to judge whether the levers are worth testing in Config or Rules.',
};

/** Dynamic Pricing → Forecast tab */
const PRICING_FORECAST_HELP = {
  overview:
    'Daily predicted occupancy for your hotel. When a saved demand forecast exists for a night, that value is used; otherwise you see a simple weekday vs weekend estimate so the series is complete across the whole range.',
  startDate: 'First night included in the series.',
  endDate: 'Last night included (inclusive). The API allows at most 366 nights per request.',
  roomType:
    'Property-wide uses forecasts stored without a room type. Picking a room type uses that type’s row when present, then falls back to property-wide, then to the weekday/weekend estimate.',
  loadButton: 'Loads the series from the server for the current dates and room-type scope.',
  chart: 'Occupancy percent by night. Hover a point for the exact date and whether the value was stored or estimated.',
  table: 'Tabular view of the same series. Source shows stored data, property-wide fallback, or model estimate.',
};

const FORECAST_MAX_SPAN_DAYS = 366;

function forecastSourceLabel(src) {
  if (src === 'estimated') return 'Estimated';
  if (src === 'property_wide') return 'Property-wide';
  return 'Stored';
}

const ForecastChartTooltip = React.memo(function ForecastChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const pct = Number(row.occupancy_pct);
  return (
    <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.paper', boxShadow: 3 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {row.fullDate}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ mt: 0.25 }}>
        {Number.isFinite(pct) ? `${pct.toFixed(1)}%` : '—'}
      </Typography>
      <Chip size="small" label={forecastSourceLabel(row.source)} sx={{ mt: 0.75, height: 22 }} />
    </Paper>
  );
});

function normalizeCompetitorHotelsPayload(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

/** Skeleton placeholder while a price-comparison card is syncing */
const PriceCompareCardSkeleton = React.memo(function PriceCompareCardSkeleton() {
  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        <Skeleton variant="circular" width={64} height={64} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton width="55%" height={22} sx={{ mb: 0.5 }} />
          <Skeleton width="85%" height={26} />
          <Skeleton width="70%" height={20} sx={{ mt: 0.5 }} />
        </Box>
      </Box>
      <Skeleton width="45%" height={36} sx={{ mb: 0.5 }} />
      <Skeleton width="50%" height={22} sx={{ mb: 1 }} />
      <Skeleton width="100%" height={16} />
      <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
        <Skeleton variant="rounded" width={64} height={24} />
        <Skeleton variant="rounded" width={72} height={24} />
        <Skeleton variant="rounded" width={68} height={24} />
      </Box>
      <Skeleton width="65%" height={14} sx={{ mt: 1 }} />
    </>
  );
});

const DYNAMIC_PRICING_TABS = [
  { icon: SpaceDashboard, label: 'Dashboard', shortLabel: 'Home' },
  { icon: TrendingUp, label: 'Recommendations', shortLabel: 'Recs' },
  { icon: Tune, label: 'Rules', shortLabel: 'Rules' },
  { icon: Event, label: 'Events', shortLabel: 'Events' },
  { icon: CompareArrows, label: 'Competitor', shortLabel: 'Comp' },
  { icon: Science, label: 'Simulate', shortLabel: 'Sim' },
  { icon: ShowChart, label: 'Forecast', shortLabel: 'Fcst' },
  { icon: Settings, label: 'Config', shortLabel: 'Cfg' },
  { icon: History, label: 'History & Logs', shortLabel: 'History' },
];

const RULE_TYPES = [
  { value: 'MIN_FLOOR', label: 'Minimum floor' },
  { value: 'MAX_CEILING', label: 'Maximum ceiling' },
  { value: 'WEEKEND_MARKUP', label: 'Weekend markup %' },
  { value: 'LAST_MINUTE_DISCOUNT', label: 'Last-minute discount %' },
  { value: 'OCCUPANCY_SURGE', label: 'Occupancy surge %' },
];

const DRAWER_INPUT_SX = {
  mb: 2,
  '& .MuiInputBase-root': { minHeight: 48 },
  '& .MuiOutlinedInput-input': { py: 1.5 },
};

const DynamicPricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getTabsFromPath = useCallback(
    () => parseDynamicPricingPath(location.pathname),
    [location.pathname],
  );

  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const isTabletLayout = isDynamicPricingTabletLayout();
  const themeColors = getDynamicPricingThemeColors(isDark);
  const drawerTheme = getBookingDrawerTheme(isDark);
  const drawerSelectMenuProps = useMemo(
    () => getDrawerSelectMenuProps(isDark, isDark ? '#2a2a31' : '#ffffff'),
    [isDark],
  );
  const drawerDatePickerSlotProps = useMemo(
    () => getDrawerDatePickerSlotProps(isDark, isDark ? '#2a2a31' : '#ffffff'),
    [isDark],
  );

  useEffect(() => watchThemeChanges(setCurrentTheme), []);

  const initialTabs = getTabsFromPath();
  const [activeTab, setActiveTab] = useState(initialTabs.activeTab);
  const [competitorSubTab, setCompetitorSubTab] = useState(initialTabs.competitorSubTab);
  const [historyLogsTab, setHistoryLogsTab] = useState(initialTabs.historyLogsTab);
  const [routeInvalid, setRouteInvalid] = useState(initialTabs.invalid);
  const [loading, setLoading] = useState(false);
  const [pageToasts, setPageToasts] = useState([]);
  const [recommendationsFetchError, setRecommendationsFetchError] = useState(false);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const recommendationsAbortRef = useRef(null);

  // Recommendations (calendar + channel + multi room type)
  const [calendarMonth, setCalendarMonth] = useState(() => moment(getHotelTodayDateString()).startOf('month'));
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState('pms');
  const [allRoomTypesSelected, setAllRoomTypesSelected] = useState(true);
  const [selectedRoomTypeIds, setSelectedRoomTypeIds] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [recommendations, setRecommendations] = useState([]);

  const pushPageToast = useCallback((message, severity = 'success') => {
    if (message == null || message === '') return;
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const createdAt = Date.now();
    setPageToasts((prev) => [...prev, { id, message: String(message), severity, createdAt }]);
    window.setTimeout(() => {
      setPageToasts((prev) => prev.filter((t) => t.id !== id));
    }, 7000);
  }, []);

  const dismissPageToast = useCallback((id) => {
    setPageToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notifyError = useCallback((msg) => pushPageToast(msg, 'error'), [pushPageToast]);
  const notifySuccess = useCallback((msg) => pushPageToast(msg, 'success'), [pushPageToast]);
  const notifyWarning = useCallback((msg) => pushPageToast(msg, 'warning'), [pushPageToast]);

  const closeSyncProgressDialog = useCallback(() => {
    setSyncProgressDialog(null);
  }, []);

  const roomTypeMap = useMemo(() => {
    const map = {};
    (roomTypes || []).forEach((rt) => {
      map[rt.id] = { name: rt.name || rt.code || `Type ${rt.id}`, image_url: rt.image_url };
    });
    return map;
  }, [roomTypes]);

  const ratePlanMap = useMemo(() => {
    const map = {};
    (ratePlans || []).forEach((rp) => {
      map[rp.id] = {
        name: rp.name || rp.title || rp.code,
        code: rp.code || rp.rate_plan_code,
      };
    });
    return map;
  }, [ratePlans]);

  // Our hotel reference rate (from recommendations) for competitor comparison
  const ourReferenceRate = useMemo(() => {
    const recs = Array.isArray(recommendations) ? recommendations : [];
    if (!recs.length) return null;
    const rates = recs.map((r) => Number(r.recommended_rate)).filter((n) => !Number.isNaN(n) && n > 0);
    if (!rates.length) return null;
    return rates.reduce((a, b) => a + b, 0) / rates.length;
  }, [recommendations]);

  // Config (default so UI renders before fetch)
  const [config, setConfig] = useState({
    occupancy_weight: 30,
    lead_time_weight: 25,
    velocity_weight: 25,
    competitor_weight: 20,
    default_min_multiplier: 0.5,
    default_max_multiplier: 2,
    use_occupancy: true,
    use_lead_time: true,
    use_velocity: true,
    use_competitor: false,
    use_forecast: false,
    use_events: true,
    cron_interval_minutes: 60,
  });

  // Rules
  const [rules, setRules] = useState([]);
  const [rulesDrawerOpen, setRulesDrawerOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [ruleFormData, setRuleFormData] = useState({ name: '', rule_type: 'MIN_FLOOR', value: '', is_active: true });
  const [savingRule, setSavingRule] = useState(false);
  const [ruleDeleteConfirm, setRuleDeleteConfirm] = useState(null);

  // Events
  const [events, setEvents] = useState([]);
  const [eventsDrawerOpen, setEventsDrawerOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [eventFormData, setEventFormData] = useState({
    name: '',
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    price_multiplier: '1',
    is_active: true,
  });
  const [savingEvent, setSavingEvent] = useState(false);
  const [eventDeleteConfirm, setEventDeleteConfirm] = useState(null);

  // Competitor
  const [competitorHotels, setCompetitorHotels] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [refreshExistingRatesLoading, setRefreshExistingRatesLoading] = useState(false);
  const [refreshExistingHotelInfoLoading, setRefreshExistingHotelInfoLoading] = useState(false);
  const [addCompetitorDrawerOpen, setAddCompetitorDrawerOpen] = useState(false);
  const [addCompetitorSaving, setAddCompetitorSaving] = useState(false);
  const [searchingCompetitorByName, setSearchingCompetitorByName] = useState(false);
  const [addCompetitorForm, setAddCompetitorForm] = useState({
    name: '',
    address: '',
    average_rate: '',
    currency: 'INR',
    source: 'manual_add',
    place_id: '',
    rating: '',
    user_rating_count: '',
    reviews: [],
    primary_photo_url: '',
    amenities: [],
  });
  const [ourHotelGoogle, setOurHotelGoogle] = useState(null);
  const [ourHotelProfileSyncLoading, setOurHotelProfileSyncLoading] = useState(false);
  const [ourHotelRateSyncLoading, setOurHotelRateSyncLoading] = useState(false);
  const [syncingCompetitorId, setSyncingCompetitorId] = useState(null);
  const [rateSyncingCompetitorId, setRateSyncingCompetitorId] = useState(null);
  const [competitorListData, setCompetitorListData] = useState([]);
  const [competitorListLoading, setCompetitorListLoading] = useState(false);
  const [competitorListPagination, setCompetitorListPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });
  const [competitorListSortConfig, setCompetitorListSortConfig] = useState({ column: 'name', direction: 'asc' });
  const [competitorSyncMenuAnchor, setCompetitorSyncMenuAnchor] = useState(null);
  const [syncProgressDialog, setSyncProgressDialog] = useState(null);
  const [fullPipelineLoading, setFullPipelineLoading] = useState(false);
  const [competitorDeleteConfirm, setCompetitorDeleteConfirm] = useState(null);
  const [deletingCompetitorId, setDeletingCompetitorId] = useState(null);
  const [mapFocusCompetitorId, setMapFocusCompetitorId] = useState(null);

  // Forecast
  const [forecasts, setForecasts] = useState([]);
  const [forecastStart, setForecastStart] = useState(moment().format('YYYY-MM-DD'));
  const [forecastEnd, setForecastEnd] = useState(moment().add(30, 'days').format('YYYY-MM-DD'));
  const [forecastRoomTypeId, setForecastRoomTypeId] = useState('');
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState(null);

  const forecastChartData = useMemo(
    () =>
      (forecasts || []).map((f) => ({
        ...f,
        occupancy_pct: Number(f.occupancy_percent),
        shortDate: moment(f.date).format('MMM D'),
        fullDate: moment(f.date).format('ddd, MMM D, YYYY'),
      })),
    [forecasts],
  );

  const forecastEstimatedCount = useMemo(
    () => (forecasts || []).filter((f) => f.source === 'estimated').length,
    [forecasts],
  );

  // Simulate
  const [simResult, setSimResult] = useState(null);
  const [simStart, setSimStart] = useState(moment().format('YYYY-MM-DD'));
  const [simEnd, setSimEnd] = useState(moment().add(7, 'days').format('YYYY-MM-DD'));
  const [simWeekendMarkup, setSimWeekendMarkup] = useState(15);
  const [simLastMinute, setSimLastMinute] = useState(10);

  // History & logs (paginated; scroll loads more)
  const [history, setHistory] = useState([]);
  const [publishLogs, setPublishLogs] = useState([]);
  const [publishLogsLoading, setPublishLogsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [publishLogsHasMore, setPublishLogsHasMore] = useState(true);
  const [historyHasMore, setHistoryHasMore] = useState(true);
  const publishLogsLenRef = useRef(0);
  const historyLenRef = useRef(0);
  const publishLogsFetchLock = useRef(false);
  const historyFetchLock = useRef(false);
  /** Avoid resetting / refetching logs on Strict Mode or effect churn; only when navigating onto History & Logs tab. */
  const prevActiveTabForLogsRef = useRef(activeTab);
  /** Fetch pricing history at most once per visit to the "History (recent)" sub-tab (empty API + effect loop caused blinking). */
  const historyLogsSubTabPrimedRef = useRef(false);
  const publishLogsScrollThrottleRef = useRef(0);
  const historyScrollThrottleRef = useRef(0);
  const publishLogsScrollBoxRef = useRef(null);
  const historyScrollBoxRef = useRef(null);

  useEffect(() => {
    const parsed = getTabsFromPath();
    setRouteInvalid(parsed.invalid);
    if (parsed.invalid) return;
    setActiveTab(parsed.activeTab);
    setCompetitorSubTab(parsed.competitorSubTab);
    setHistoryLogsTab(parsed.historyLogsTab);
  }, [getTabsFromPath]);

  const handleMainTabChange = useCallback(
    (_, tabIndex) => {
      const path = buildDynamicPricingPath(tabIndex, 0, 0);
      navigate(path);
    },
    [navigate],
  );

  const handleCompetitorSubTabChange = useCallback(
    (_, subTabIndex) => {
      const path = buildDynamicPricingPath(4, subTabIndex, 0);
      navigate(path);
    },
    [navigate],
  );

  const handleHistoryLogsTabChange = useCallback(
    (_, subTabIndex) => {
      const path = buildDynamicPricingPath(8, 0, subTabIndex);
      navigate(path);
    },
    [navigate],
  );

  const openCompetitorMapForRow = useCallback(
    (competitorId) => {
      if (competitorId == null) return;
      setMapFocusCompetitorId(competitorId);
      navigate(buildDynamicPricingPath(4, 2, 0));
    },
    [navigate],
  );

  const fetchRoomTypes = useCallback(async () => {
    try {
      const list = await getConfigurationRoomTypes();
      setRoomTypes(Array.isArray(list) ? list : []);
    } catch (err) {
      notifyError(err?.error || 'Failed to load room types');
      setRoomTypes([]);
    }
  }, [notifyError]);

  const fetchChannels = useCallback(async () => {
    try {
      const data = await getChannels();
      setChannels(data?.channels ?? []);
    } catch (err) {
      setChannels([]);
    }
  }, []);

  const fetchRatePlans = useCallback(async () => {
    try {
      const channelParam = selectedChannelId === 'pms' || !selectedChannelId ? null : selectedChannelId;
      const data = await getRatePlans(channelParam);
      const list = Array.isArray(data) ? data : (data?.rate_plans || []);
      setRatePlans(Array.isArray(list) ? list : []);
    } catch (err) {
      setRatePlans([]);
    }
  }, [selectedChannelId]);

  const fetchRecommendations = useCallback(async () => {
    const monthStart = calendarMonth.clone().startOf('month');
    const today = moment(getHotelTodayDateString(), 'YYYY-MM-DD').startOf('day');
    const rangeStart = moment.max(monthStart, today);
    const rangeEnd = calendarMonth.clone().endOf('month');
    if (rangeStart.isAfter(rangeEnd, 'day')) {
      setRecommendations([]);
      setRecommendationsFetchError(false);
      setRecommendationsLoading(false);
      return;
    }
    const start = rangeStart.format('YYYY-MM-DD');
    const end = rangeEnd.format('YYYY-MM-DD');

    if (recommendationsAbortRef.current) {
      recommendationsAbortRef.current.abort();
    }
    const controller = new AbortController();
    recommendationsAbortRef.current = controller;

    setRecommendationsLoading(true);
    setRecommendationsFetchError(false);
    try {
      const params = { start_date: start, end_date: end };
      if (selectedChannelId && selectedChannelId !== '') {
        params.channel_id = selectedChannelId;
      }
      if (!allRoomTypesSelected && selectedRoomTypeIds.length > 0) {
        params.room_type_ids = selectedRoomTypeIds.join(',');
      }
      const data = await getPricingRecommendations(params, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setRecommendations(Array.isArray(data.recommendations) ? data.recommendations : []);
    } catch (err) {
      if (controller.signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return;
      }
      setRecommendationsFetchError(true);
      notifyError(err?.error || err?.message || 'Failed to load recommendations');
      setRecommendations([]);
    } finally {
      if (!controller.signal.aborted) {
        setRecommendationsLoading(false);
      }
    }
  }, [calendarMonth, selectedChannelId, allRoomTypesSelected, selectedRoomTypeIds, notifyError]);

  const handleApply = useCallback(async (recsToApply) => {
    const recs = Array.isArray(recsToApply) ? recsToApply : (Array.isArray(recommendations) ? recommendations : []);
    if (!recs.length) return;
    try {
      const rates = recs.map((r) => ({
        room_type_id: r.room_type_id,
        rate_plan_id: r.rate_plan_id,
        rate_date: typeof r.rate_date === 'string' ? r.rate_date : moment(r.rate_date).format('YYYY-MM-DD'),
        base_rate: r.recommended_rate,
      }));
      const payload = { rates, reason: 'Dynamic pricing apply' };
      if (selectedChannelId && selectedChannelId !== '') {
        payload.channel_id = selectedChannelId;
      }
      await applyPricingRates(payload);
      const targetName = selectedChannelId === 'pms' || !selectedChannelId
        ? 'PMS'
        : (channels.find((c) => String(c.id) === String(selectedChannelId))?.name || 'channel');
      notifySuccess(`Applied ${rates.length} rate${rates.length === 1 ? '' : 's'} to ${targetName}.`);
    } catch (err) {
      notifyError(err?.error || err?.message || 'Failed to apply rates');
      throw err;
    }
  }, [recommendations, selectedChannelId, channels, notifySuccess, notifyError]);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await getPricingConfig();
      setConfig((prev) => (data ? { ...prev, ...data } : prev));
    } catch (err) {
      notifyError(err?.error || 'Failed to load config');
    }
  }, [notifyError]);

  const fetchRules = useCallback(async () => {
    try {
      const data = await getPricingRules();
      setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      notifyError(err?.error || 'Failed to load rules');
    }
  }, [notifyError]);

  const openRulesDrawer = useCallback((rule = null) => {
    if (rule) {
      setEditingRuleId(rule.id);
      setRuleFormData({
        name: rule.name || '',
        rule_type: rule.rule_type || 'MIN_FLOOR',
        value: rule.value != null ? String(rule.value) : '',
        is_active: rule.is_active !== false,
      });
    } else {
      setEditingRuleId(null);
      setRuleFormData({ name: '', rule_type: 'MIN_FLOOR', value: '', is_active: true });
    }
    setRulesDrawerOpen(true);
  }, []);

  const closeRulesDrawer = useCallback(() => {
    setRulesDrawerOpen(false);
    setEditingRuleId(null);
    setRuleFormData({ name: '', rule_type: 'MIN_FLOOR', value: '', is_active: true });
  }, []);

  const saveRule = useCallback(async () => {
    const name = (ruleFormData.name || '').trim();
    const value = ruleFormData.value === '' ? null : Number(ruleFormData.value);
    if (!name) {
      notifyWarning('Rule name is required');
      return;
    }
    if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
      notifyWarning('Value is required');
      return;
    }
    setSavingRule(true);
    try {
      if (editingRuleId) {
        await updatePricingRule(editingRuleId, { name, rule_type: ruleFormData.rule_type, value, is_active: ruleFormData.is_active });
      } else {
        await createPricingRule({ name, rule_type: ruleFormData.rule_type, value, is_active: ruleFormData.is_active });
      }
      await fetchRules();
      closeRulesDrawer();
    } catch (err) {
      notifyError(err?.error || err?.message || 'Failed to save rule');
    } finally {
      setSavingRule(false);
    }
  }, [editingRuleId, ruleFormData, closeRulesDrawer, fetchRules, notifyWarning, notifyError]);

  const handleDeleteRule = useCallback(async (id) => {
    setRuleDeleteConfirm(null);
    try {
      await deletePricingRule(id);
      await fetchRules();
    } catch (err) {
      notifyError(err?.error || err?.message || 'Failed to delete rule');
    }
  }, [fetchRules, notifyError]);

  const fetchEvents = useCallback(async () => {
    try {
      const data = await getPricingEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      notifyError(err?.error || 'Failed to load events');
    }
  }, [notifyError]);

  const openEventsDrawer = useCallback((event = null) => {
    if (event) {
      setEditingEventId(event.id);
      setEventFormData({
        name: event.name || '',
        start_date: event.start_date || moment().format('YYYY-MM-DD'),
        end_date: event.end_date || moment().format('YYYY-MM-DD'),
        price_multiplier: event.price_multiplier != null ? String(event.price_multiplier) : '1',
        is_active: event.is_active !== false,
      });
    } else {
      setEditingEventId(null);
      setEventFormData({
        name: '',
        start_date: moment().format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
        price_multiplier: '1',
        is_active: true,
      });
    }
    setEventsDrawerOpen(true);
  }, []);

  const closeEventsDrawer = useCallback(() => {
    setEventsDrawerOpen(false);
    setEditingEventId(null);
    setEventFormData({
      name: '',
      start_date: moment().format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
      price_multiplier: '1',
      is_active: true,
    });
  }, []);

  const saveEvent = useCallback(async () => {
    const name = (eventFormData.name || '').trim();
    if (!name) {
      notifyWarning('Event name is required');
      return;
    }
    const mult = eventFormData.price_multiplier === '' ? null : Number(eventFormData.price_multiplier);
    if (mult === null || isNaN(mult) || mult < 0.01) {
      notifyWarning('Price multiplier must be at least 0.01');
      return;
    }
    setSavingEvent(true);
    try {
      const payload = {
        name,
        start_date: eventFormData.start_date,
        end_date: eventFormData.end_date,
        price_multiplier: mult,
        is_active: eventFormData.is_active,
      };
      if (editingEventId) {
        await updatePricingEvent(editingEventId, payload);
      } else {
        await createPricingEvent(payload);
      }
      await fetchEvents();
      closeEventsDrawer();
    } catch (err) {
      notifyError(err?.error || err?.message || 'Failed to save event');
    } finally {
      setSavingEvent(false);
    }
  }, [editingEventId, eventFormData, closeEventsDrawer, fetchEvents, notifyWarning, notifyError]);

  const handleDeleteEvent = useCallback(async (id) => {
    setEventDeleteConfirm(null);
    try {
      await deletePricingEvent(id);
      await fetchEvents();
    } catch (err) {
      notifyError(err?.error || err?.message || 'Failed to delete event');
    }
  }, [fetchEvents, notifyError]);

  const fetchCompetitor = useCallback(async () => {
    const [hotelsSettled, snapshotSettled] = await Promise.allSettled([
      getCompetitorHotels(),
      getOurHotelGoogleSnapshot().catch(() => null),
    ]);
    if (hotelsSettled.status === 'fulfilled') {
      const hotelsData = hotelsSettled.value;
      const hotelList = Array.isArray(hotelsData)
        ? hotelsData
        : Array.isArray(hotelsData?.data)
          ? hotelsData.data
          : Array.isArray(hotelsData?.results)
            ? hotelsData.results
            : [];
      setCompetitorHotels(hotelList);
    } else {
      setCompetitorHotels([]);
      notifyError(hotelsSettled.reason?.error || hotelsSettled.reason?.message || 'Failed to load competitor list');
    }
    if (snapshotSettled.status === 'fulfilled' && snapshotSettled.value != null) {
      setOurHotelGoogle(snapshotSettled.value);
    }
  }, [notifyError]);

  const handleSyncOurHotelProfile = useCallback(async () => {
    setOurHotelProfileSyncLoading(true);
    try {
      const result = await syncOurHotelProfileFromPlaces();
      setOurHotelGoogle(result);
      notifySuccess('Hotel profile saved from Google Places (rating, photo, reviews).');
    } catch (err) {
      notifyError(err?.error || err?.message || 'Profile sync failed');
    } finally {
      setOurHotelProfileSyncLoading(false);
    }
  }, []);

  const handleSyncOurHotelRate = useCallback(async () => {
    setOurHotelRateSyncLoading(true);
    try {
      const result = await fetchOurHotelFromGoogle();
      setOurHotelGoogle(result);
      if (result?.google_rate != null) {
        notifySuccess('Latest nightly rate synced via AI.');
      } else {
        notifySuccess('Rate sync finished — no public rate found for tonight.');
      }
    } catch (err) {
      notifyError(err?.error || err?.message || 'Rate sync failed');
    } finally {
      setOurHotelRateSyncLoading(false);
    }
  }, [notifySuccess, notifyError]);

  const fetchCompetitorList = useCallback(async () => {
    setCompetitorListLoading(true);
    try {
      const sortParam = competitorListSortConfig.direction === 'desc'
        ? `-${competitorListSortConfig.column}`
        : competitorListSortConfig.column;
      const params = {
        page: competitorListPagination.page + 1,
        per_page: competitorListPagination.perPage,
        sort: sortParam,
      };
      const response = await getCompetitorHotelsList(params);
      const data = response?.Data ?? response?.data ?? [];
      setCompetitorListData(Array.isArray(data) ? data : []);
      setCompetitorListPagination((prev) => ({
        ...prev,
        totalRecords: response?.total_records ?? response?.count ?? 0,
      }));
    } catch (err) {
      notifyError(err?.error || err?.message || 'Failed to load competitor list');
      setCompetitorListData([]);
    } finally {
      setCompetitorListLoading(false);
    }
  }, [competitorListPagination.page, competitorListPagination.perPage, competitorListSortConfig, notifyError]);

  useEffect(() => {
    if (competitorSubTab === 1) fetchCompetitorList();
  }, [competitorSubTab, fetchCompetitorList]);

  const handleCompetitorListPageChange = useCallback((newPage) => {
    setCompetitorListPagination((prev) => ({ ...prev, page: newPage }));
  }, []);
  const handleCompetitorListPerPageChange = useCallback((newPerPage) => {
    setCompetitorListPagination((prev) => ({ ...prev, perPage: newPerPage, page: 0 }));
  }, []);
  const handleCompetitorListSortChange = useCallback((newSortConfig) => {
    setCompetitorListSortConfig(newSortConfig);
    setCompetitorListPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleSyncCompetitor = useCallback(
    async (competitorId) => {
      await runCompetitorRowSync({
        competitorId,
        syncFn: syncCompetitorHotel,
        setBusyId: setSyncingCompetitorId,
        notifySuccess,
        notifyError,
        setCompetitorHotels,
        fetchCompetitorList,
        competitorSubTab,
        successMessage: 'Hotel profile synced from Google Places.',
        errorFallback: 'Failed to sync competitor',
      });
    },
    [competitorSubTab, fetchCompetitorList, notifySuccess, notifyError],
  );

  const handleSyncCompetitorRateOnly = useCallback(
    async (competitorId) => {
      await runCompetitorRowSync({
        competitorId,
        syncFn: syncCompetitorHotelRateOnly,
        setBusyId: setRateSyncingCompetitorId,
        notifySuccess,
        notifyError,
        setCompetitorHotels,
        fetchCompetitorList,
        competitorSubTab,
        successMessage: 'Daily rate synced via AI.',
        errorFallback: 'Failed to sync rate',
      });
    },
    [competitorSubTab, fetchCompetitorList, notifySuccess, notifyError],
  );

  const handleDeleteCompetitor = useCallback(
    async (id) => {
      if (id == null) return;
      setDeletingCompetitorId(id);
      try {
        await deleteCompetitorHotel(id);
        setCompetitorDeleteConfirm(null);
        await fetchCompetitor();
        if (competitorSubTab === 1) {
          await fetchCompetitorList();
        }
        notifySuccess('Competitor and stored rates removed.');
      } catch (err) {
        notifyError(err?.error || err?.message || 'Failed to delete competitor');
      } finally {
        setDeletingCompetitorId(null);
      }
    },
    [competitorSubTab, fetchCompetitor, fetchCompetitorList, notifySuccess, notifyError],
  );

  const COMPETITOR_SYNC_DELAY_SEC = 20;

  const handleDiscoverAndFullSync = useCallback(async () => {
    setFullPipelineLoading(true);
    setImportLoading(true);
    setRefreshExistingRatesLoading(true);
    setRefreshExistingHotelInfoLoading(true);
    setSyncProgressDialog({
      open: true,
      title: 'Full pipeline sync',
      itemProgress: null,
      steps: [
        { id: 'discover', label: 'Discover nearby hotels', status: 'running', sublabel: 'Places / OSM search…' },
        { id: 'rates', label: 'Sync AI competitor rates', status: 'pending', sublabel: '' },
        { id: 'profiles', label: 'Sync Google Places profiles', status: 'pending', sublabel: '' },
      ],
      footerNote:
        'Each competitor runs as its own request. ~20s pause between calls. Large lists take many minutes — keep this tab open.',
      errorMessage: null,
      completed: false,
    });

    try {
      const disc = await autoDiscoverCompetitors({ max_results: 15 });
      const imported = disc?.imported ?? 0;
      const discoverUpdated = disc?.updated ?? 0;
      const discoverSublabel =
        imported > 0
          ? `${imported} new hotel(s) imported`
          : discoverUpdated > 0
            ? `${discoverUpdated} existing refreshed (no duplicates)`
            : 'No changes';
      setSyncProgressDialog((prev) => ({
        ...prev,
        itemProgress: null,
        steps: [
          {
            id: 'discover',
            label: 'Discover nearby hotels',
            status: 'done',
            sublabel: discoverSublabel,
          },
          {
            id: 'rates',
            label: 'Sync AI competitor rates',
            status: 'running',
            sublabel: 'Loading competitor list…',
          },
          { id: 'profiles', label: 'Sync Google Places profiles', status: 'pending', sublabel: '' },
        ],
      }));

      await fetchCompetitor();
      const hotelsPayload = await getCompetitorHotels();
      const list = normalizeCompetitorHotelsPayload(hotelsPayload);
      const rateRows = list.map((c) => ({ id: c.id, name: c.name })).filter((r) => r.id != null);

      setSyncProgressDialog((prev) => ({
        ...prev,
        steps: [
          prev.steps[0],
          {
            id: 'rates',
            label: 'Sync AI competitor rates',
            status: 'running',
            sublabel: rateRows.length ? `0 / ${rateRows.length} competitors` : 'No competitors to sync',
          },
          prev.steps[2],
        ],
      }));

      const rateResult = await runSequentialCompetitorSync(
        rateRows,
        async (row) => {
          const updated = await syncCompetitorHotelRateOnly(row.id);
          if (updated && typeof updated === 'object') {
            setCompetitorHotels((prev) => prev.map((x) => (x.id === row.id ? { ...x, ...updated } : x)));
          }
        },
        {
          delayMs: COMPETITOR_SYNC_DELAY_SEC * 1000,
          onProgress: ({ index, total, row }) => {
            setSyncProgressDialog((prev) => ({
              ...prev,
              itemProgress: {
                current: index,
                total,
                label: row?.name ? String(row.name) : `Competitor ${row.id}`,
              },
              steps: prev.steps.map((s) =>
                s.id === 'rates'
                  ? {
                      ...s,
                      sublabel: `Rate ${index} / ${total}${row?.name ? ` · ${row.name}` : ''}`,
                    }
                  : s,
              ),
            }));
          },
        },
      );

      setSyncProgressDialog((prev) => ({
        ...prev,
        itemProgress: null,
        steps: prev.steps.map((s) =>
          s.id === 'rates' ? { ...s, sublabel: 'Updating your hotel AI rate…' } : s,
        ),
      }));

      let ourMsg = '';
      try {
        const ours = await fetchOurHotelFromGoogle();
        if (ours) {
          setOurHotelGoogle(ours);
          ourMsg = ' Your hotel rate updated.';
        }
      } catch {
        ourMsg = ' Your hotel rate sync skipped or failed.';
      }

      setSyncProgressDialog((prev) => ({
        ...prev,
        steps: [
          prev.steps[0],
          {
            ...prev.steps[1],
            status: 'done',
            sublabel: `${rateResult.successCount} rate OK, ${rateResult.failCount} failed (of ${rateResult.total}).${ourMsg}${formatSyncFailureHint(rateResult.failures)}`,
          },
          {
            id: 'profiles',
            label: 'Sync Google Places profiles',
            status: 'running',
            sublabel: rateRows.length ? `0 / ${rateRows.length}` : 'No competitors',
          },
        ],
      }));

      const profResult = await runSequentialCompetitorSync(
        rateRows,
        async (row) => {
          const updated = await syncCompetitorHotel(row.id);
          if (updated && typeof updated === 'object') {
            setCompetitorHotels((prev) => prev.map((x) => (x.id === row.id ? { ...x, ...updated } : x)));
          }
        },
        {
          delayMs: COMPETITOR_SYNC_DELAY_SEC * 1000,
          onProgress: ({ index, total, row }) => {
            setSyncProgressDialog((prev) => ({
              ...prev,
              itemProgress: {
                current: index,
                total,
                label: row?.name ? String(row.name) : `Competitor ${row.id}`,
              },
              steps: prev.steps.map((s) =>
                s.id === 'profiles'
                  ? {
                      ...s,
                      sublabel: `Profile ${index} / ${total}${row?.name ? ` · ${row.name}` : ''}`,
                    }
                  : s,
              ),
            }));
          },
        },
      );

      await fetchCompetitor();
      if (competitorSubTab === 1) await fetchCompetitorList();

      setSyncProgressDialog((prev) => ({
        ...prev,
        itemProgress: null,
        completed: true,
        steps: [
          prev.steps[0],
          prev.steps[1],
          {
            id: 'profiles',
            label: 'Sync Google Places profiles',
            status: 'done',
            sublabel: `${profResult.successCount} profile OK, ${profResult.failCount} failed (of ${profResult.total})`,
          },
        ],
      }));

      notifySuccess(
        `Full pipeline finished: ${imported} new · ${discoverUpdated} existing updated · rates ${rateResult.successCount} OK / ${rateResult.failCount} failed · profiles ${profResult.successCount} OK / ${profResult.failCount} failed`,
      );
    } catch (err) {
      notifyError(err?.error || err?.message || 'Full pipeline failed');
      setSyncProgressDialog((prev) =>
        prev
          ? {
              ...prev,
              itemProgress: null,
              completed: true,
              errorMessage: err?.error || err?.message || 'Sync failed. Check network and server logs.',
              steps: prev.steps.map((s) => (s.status === 'running' ? { ...s, status: 'error' } : s)),
            }
          : prev,
      );
    } finally {
      setFullPipelineLoading(false);
      setImportLoading(false);
      setRefreshExistingRatesLoading(false);
      setRefreshExistingHotelInfoLoading(false);
    }
  }, [
    fetchCompetitor,
    fetchCompetitorList,
    competitorSubTab,
    notifySuccess,
    notifyError,
  ]);

  const handleCompetitorRefresh = useCallback(async () => {
    setImportLoading(true);
    setSyncProgressDialog({
      open: true,
      title: 'Discover nearby hotels',
      itemProgress: null,
      steps: [{ id: 'discover', label: 'Searching Places & OpenStreetMap…', status: 'running', sublabel: '' }],
      footerNote: 'New competitors are saved without nightly rates until you run a rate sync.',
      errorMessage: null,
      completed: false,
    });
    try {
      const result = await autoDiscoverCompetitors({ max_results: 15 });
      await fetchCompetitor();
      if (competitorSubTab === 1) await fetchCompetitorList();
      const newCount = result?.imported ?? 0;
      const updatedCount = result?.updated ?? 0;
      const hint = result?.hint ? String(result.hint) : '';
      let msg =
        newCount > 0
          ? `Discovered ${newCount} new nearby hotel(s) (no rates). List updated.`
          : updatedCount > 0
            ? `Discover complete: ${updatedCount} existing competitor(s) refreshed — no duplicates added. List updated.`
            : 'Discover complete. No new hotels found.';
      if (hint) msg += ` ${hint}`;
      const discoverDoneSublabel =
        newCount > 0
          ? `${newCount} new hotel(s) added`
          : updatedCount > 0
            ? `${updatedCount} existing refreshed (no duplicates)`
            : 'No new hotels matched';
      setSyncProgressDialog({
        open: true,
        title: 'Discover complete',
        itemProgress: null,
        completed: true,
        steps: [
          {
            id: 'discover',
            label: 'Discover nearby hotels',
            status: 'done',
            sublabel: discoverDoneSublabel,
          },
        ],
        footerNote: hint || 'Use “All rates” or “Full pipeline” to refresh AI prices.',
        errorMessage: null,
      });
      notifySuccess(msg);
    } catch (err) {
      notifyError(err?.error || err?.message || 'Import failed');
      setSyncProgressDialog((prev) =>
        prev
          ? {
              ...prev,
              itemProgress: null,
              completed: true,
              errorMessage: err?.error || err?.message || 'Discover failed.',
              steps: [{ id: 'discover', label: 'Discover nearby hotels', status: 'error', sublabel: '' }],
            }
          : prev,
      );
    } finally {
      setImportLoading(false);
    }
  }, [
    fetchCompetitor,
    fetchCompetitorList,
    competitorSubTab,
    notifySuccess,
    notifyError,
  ]);

  /** Bulk: daily cadence — AI rates only (matches cron `refresh_competitors_daily`). */
  const handleBulkSyncCompetitorRates = useCallback(async () => {
    setRefreshExistingRatesLoading(true);
    const rateRows = (competitorHotels || [])
      .map((c) => ({ id: c.id, name: c.name }))
      .filter((r) => r.id != null);

    setSyncProgressDialog({
      open: true,
      title: 'Sync all rates',
      itemProgress: null,
      steps: [
        {
          id: 'rates',
          label: 'AI rate sync (Gemini)',
          status: 'running',
          sublabel: rateRows.length ? `0 / ${rateRows.length}` : 'No competitors in list',
        },
      ],
      footerNote: `~${COMPETITOR_SYNC_DELAY_SEC}s between each competitor to reduce AI rate limits.`,
      errorMessage: null,
      completed: false,
    });

    try {
      const result = await runSequentialCompetitorSync(
        rateRows,
        async (row) => {
          const updated = await syncCompetitorHotelRateOnly(row.id);
          if (updated && typeof updated === 'object') {
            setCompetitorHotels((prev) => prev.map((x) => (x.id === row.id ? { ...x, ...updated } : x)));
          }
        },
        {
          delayMs: COMPETITOR_SYNC_DELAY_SEC * 1000,
          onProgress: ({ index, total, row }) => {
            setSyncProgressDialog((prev) => ({
              ...prev,
              itemProgress: {
                current: index,
                total,
                label: row?.name ? String(row.name) : `Competitor ${row.id}`,
              },
              steps: [
                {
                  id: 'rates',
                  label: 'AI rate sync (Gemini)',
                  status: 'running',
                  sublabel: `${index} / ${total}${row?.name ? ` · ${row.name}` : ''}`,
                },
              ],
            }));
          },
        },
      );

      setSyncProgressDialog((prev) => ({
        ...prev,
        itemProgress: null,
        steps: prev.steps.map((s) =>
          s.id === 'rates' ? { ...s, sublabel: 'Updating your hotel AI rate…' } : s,
        ),
      }));

      let ourMsg = '';
      try {
        const ours = await fetchOurHotelFromGoogle();
        if (ours) {
          setOurHotelGoogle(ours);
          ourMsg = ' Your hotel rate updated.';
        }
      } catch {
        ourMsg = ' (Your hotel rate sync skipped or failed.)';
      }

      await fetchCompetitor();
      if (competitorSubTab === 1) await fetchCompetitorList();

      const failHint = formatSyncFailureHint(result.failures);
      setSyncProgressDialog({
        open: true,
        title: 'Rate sync complete',
        itemProgress: null,
        completed: true,
        steps: [
          {
            id: 'rates',
            label: 'AI rate sync (Gemini)',
            status: 'done',
            sublabel: `${result.successCount} OK, ${result.failCount} failed (of ${result.total}).${ourMsg}${failHint}`,
          },
        ],
        footerNote: '',
        errorMessage: result.failCount ? failHint.trim() || null : null,
      });
      if (result.failCount) {
        notifyError(`Daily rate sync: ${result.successCount} OK, ${result.failCount} failed.${failHint}${ourMsg}`);
      } else {
        notifySuccess(`Daily rate sync: ${result.successCount} competitor(s) OK.${ourMsg}`);
      }
    } catch (err) {
      notifyError(err?.error || err?.message || 'Failed to sync competitor rates');
      setSyncProgressDialog((prev) =>
        prev
          ? {
              ...prev,
              itemProgress: null,
              completed: true,
              errorMessage: err?.error || err?.message || 'Rate sync failed.',
              steps: [{ id: 'rates', label: 'Daily rate capture', status: 'error', sublabel: '' }],
            }
          : prev,
      );
    } finally {
      setRefreshExistingRatesLoading(false);
    }
  }, [
    competitorHotels,
    fetchCompetitor,
    fetchCompetitorList,
    competitorSubTab,
    notifySuccess,
    notifyError,
  ]);

  /** Bulk: monthly cadence — Places profile only (matches `refresh_competitors_monthly`). */
  const handleBulkSyncCompetitorHotelInfo = useCallback(async () => {
    setRefreshExistingHotelInfoLoading(true);
    const profileRows = (competitorHotels || [])
      .map((c) => ({ id: c.id, name: c.name }))
      .filter((r) => r.id != null);

    setSyncProgressDialog({
      open: true,
      title: 'Sync all hotel profiles',
      itemProgress: null,
      steps: [
        {
          id: 'profiles',
          label: 'Google Places profile (metadata, photos, reviews)',
          status: 'running',
          sublabel: profileRows.length ? `0 / ${profileRows.length}` : 'No competitors in list',
        },
      ],
      footerNote: 'No nightly rates in this step — use “All rates” for AI prices.',
      errorMessage: null,
      completed: false,
    });

    try {
      const result = await runSequentialCompetitorSync(
        profileRows,
        async (row) => {
          const updated = await syncCompetitorHotel(row.id);
          if (updated && typeof updated === 'object') {
            setCompetitorHotels((prev) => prev.map((x) => (x.id === row.id ? { ...x, ...updated } : x)));
          }
        },
        {
          delayMs: COMPETITOR_SYNC_DELAY_SEC * 1000,
          onProgress: ({ index, total, row }) => {
            setSyncProgressDialog((prev) => ({
              ...prev,
              itemProgress: {
                current: index,
                total,
                label: row?.name ? String(row.name) : `Competitor ${row.id}`,
              },
              steps: [
                {
                  id: 'profiles',
                  label: 'Google Places profile (metadata, photos, reviews)',
                  status: 'running',
                  sublabel: `${index} / ${total}${row?.name ? ` · ${row.name}` : ''}`,
                },
              ],
            }));
          },
        },
      );

      await fetchCompetitor();
      if (competitorSubTab === 1) await fetchCompetitorList();

      setSyncProgressDialog({
        open: true,
        title: 'Profile sync complete',
        itemProgress: null,
        completed: true,
        steps: [
          {
            id: 'profiles',
            label: 'Google Places profile (metadata, photos, reviews)',
            status: 'done',
            sublabel: `${result.successCount} OK, ${result.failCount} failed (of ${result.total})`,
          },
        ],
        footerNote: '',
        errorMessage: null,
      });
      notifySuccess(
        `Places profile sync: ${result.successCount} OK, ${result.failCount} failed (of ${result.total}).`,
      );
    } catch (err) {
      notifyError(err?.error || err?.message || 'Failed to sync competitor hotel profiles');
      setSyncProgressDialog((prev) =>
        prev
          ? {
              ...prev,
              itemProgress: null,
              completed: true,
              errorMessage: err?.error || err?.message || 'Profile sync failed.',
              steps: [{ id: 'profiles', label: 'Google Places profile', status: 'error', sublabel: '' }],
            }
          : prev,
      );
    } finally {
      setRefreshExistingHotelInfoLoading(false);
    }
  }, [
    competitorHotels,
    fetchCompetitor,
    fetchCompetitorList,
    competitorSubTab,
    notifySuccess,
    notifyError,
  ]);

  const openAddCompetitorDrawer = useCallback(() => {
    setAddCompetitorForm({
      name: '',
      address: '',
      average_rate: '',
      currency: 'INR',
      source: 'manual_add',
      place_id: '',
      rating: '',
      user_rating_count: '',
      reviews: [],
      primary_photo_url: '',
      amenities: [],
    });
    setAddCompetitorDrawerOpen(true);
  }, []);

  const closeAddCompetitorDrawer = useCallback(() => {
    if (addCompetitorSaving) return;
    setAddCompetitorDrawerOpen(false);
  }, [addCompetitorSaving]);

  const handleSearchCompetitorByName = useCallback(async () => {
    const name = (addCompetitorForm.name || '').trim();
    if (!name) return;
    setSearchingCompetitorByName(true);
    try {
      const data = await searchCompetitorByName(name);
      setAddCompetitorForm((prev) => ({
        ...prev,
        name: data?.name || prev.name,
        address: data?.address || prev.address,
        place_id: data?.place_id || '',
        rating: data?.rating ?? '',
        user_rating_count: data?.user_rating_count ?? '',
        reviews: Array.isArray(data?.reviews) ? data.reviews : [],
        primary_photo_url: data?.primary_photo_url || '',
        amenities: Array.isArray(data?.amenities) ? data.amenities : [],
      }));
      notifySuccess('Competitor details autofilled from Google.');
    } catch (err) {
      notifyError(err?.error || err?.message || 'No match found on Google Places');
    } finally {
      setSearchingCompetitorByName(false);
    }
  }, [addCompetitorForm.name, notifySuccess, notifyError]);

  const handleAddCompetitorSave = useCallback(async () => {
    const name = (addCompetitorForm.name || '').trim();
    if (!name) {
      notifyWarning('Competitor hotel name is required');
      return;
    }
    const normalizePlaceId = (raw) => {
      const s = String(raw || '').trim();
      return s.startsWith('places/') ? s.slice(7).trim() : s;
    };
    const selected = typeof window !== 'undefined' ? window.DJANGO_CONTEXT?.selectedHotel : null;
    const propName = (selected?.name || '').trim().toLowerCase();
    if (propName && name.toLowerCase() === propName) {
      notifyWarning('You cannot add your own property as a competitor.');
      return;
    }
    const ourPid = normalizePlaceId(ourHotelGoogle?.place_id);
    const candPid = normalizePlaceId(addCompetitorForm.place_id);
    if (ourPid && candPid && ourPid === candPid) {
      notifyWarning('You cannot add your own property as a competitor.');
      return;
    }
    setAddCompetitorSaving(true);
    try {
      const payload = {
        name,
        address: addCompetitorForm.address || '',
        average_rate: addCompetitorForm.average_rate === '' ? null : Number(addCompetitorForm.average_rate),
        currency: addCompetitorForm.currency || 'INR',
        source: addCompetitorForm.source || 'manual_add',
        place_id: addCompetitorForm.place_id || '',
        rating: addCompetitorForm.rating === '' ? null : Number(addCompetitorForm.rating),
        user_rating_count: addCompetitorForm.user_rating_count === '' ? null : Number(addCompetitorForm.user_rating_count),
        reviews: addCompetitorForm.reviews || [],
        primary_photo_url: addCompetitorForm.primary_photo_url || '',
        amenities: addCompetitorForm.amenities || [],
      };
      const created = await createCompetitorHotel(payload);
      // Optimistic: show new card immediately (backend returns full object)
      if (created && typeof created === 'object' && created.id != null) {
        setCompetitorHotels((prev) => [created, ...prev.filter((c) => c.id !== created.id)]);
      }
      await fetchCompetitor();
      if (competitorSubTab === 1) fetchCompetitorList();
      setAddCompetitorDrawerOpen(false);
      notifySuccess('Competitor added successfully.');
    } catch (err) {
      notifyError(err?.error || err?.message || 'Failed to add competitor');
    } finally {
      setAddCompetitorSaving(false);
    }
  }, [addCompetitorForm, ourHotelGoogle, fetchCompetitor, fetchCompetitorList, competitorSubTab, notifyWarning, notifySuccess, notifyError]);

  const fetchForecast = useCallback(async () => {
    const startM = moment(forecastStart, 'YYYY-MM-DD', true);
    const endM = moment(forecastEnd, 'YYYY-MM-DD', true);
    if (!startM.isValid() || !endM.isValid()) {
      notifyWarning('Please choose valid start and end dates.');
      return;
    }
    if (endM.isBefore(startM)) {
      notifyWarning('End date must be on or after the start date.');
      return;
    }
    if (endM.diff(startM, 'days') + 1 > FORECAST_MAX_SPAN_DAYS) {
      notifyWarning(`Date range cannot exceed ${FORECAST_MAX_SPAN_DAYS} days.`);
      return;
    }
    const params = { start_date: forecastStart, end_date: forecastEnd };
    if (forecastRoomTypeId !== '' && forecastRoomTypeId != null) {
      params.room_type_id = Number(forecastRoomTypeId);
    }
    setForecastLoading(true);
    setForecastError(null);
    try {
      const data = await getPricingForecast(params);
      setForecasts(Array.isArray(data.forecasts) ? data.forecasts : []);
    } catch (err) {
      const msg = err?.error || err?.message || 'Failed to load forecast';
      setForecastError(msg);
      notifyError(msg);
      setForecasts([]);
    } finally {
      setForecastLoading(false);
    }
  }, [forecastStart, forecastEnd, forecastRoomTypeId, notifyError, notifyWarning]);

  const runSimulation = useCallback(async () => {
    setLoading(true);
    setSimResult(null);
    try {
      const data = await runPricingSimulation({
        start_date: simStart,
        end_date: simEnd,
        weekend_markup_pct: simWeekendMarkup,
        last_minute_discount_pct: simLastMinute,
        last_minute_days: 3,
      });
      setSimResult(data);
    } catch (err) {
      notifyError(err?.error || 'Failed to run simulation');
    } finally {
      setLoading(false);
    }
  }, [simStart, simEnd, simWeekendMarkup, simLastMinute, notifyError]);

  useEffect(() => {
    publishLogsLenRef.current = publishLogs.length;
  }, [publishLogs.length]);

  useEffect(() => {
    historyLenRef.current = history.length;
  }, [history.length]);

  const fetchPublishLogsPage = useCallback(
    async (offset) => {
      if (publishLogsFetchLock.current) return;
      publishLogsFetchLock.current = true;
      setPublishLogsLoading(true);
      try {
        const data = await getPublishLogs({ limit: HISTORY_LOG_PAGE_SIZE, offset });
        const rows = Array.isArray(data?.results) ? data.results : [];
        const hasMore = Boolean(data?.has_more);
        setPublishLogs((prev) => (offset === 0 ? rows : [...prev, ...rows]));
        setPublishLogsHasMore(rows.length > 0 ? hasMore : false);
      } catch (err) {
        notifyError(err?.error || err?.message || 'Failed to load publish logs');
        if (offset === 0) setPublishLogs([]);
        setPublishLogsHasMore(false);
      } finally {
        publishLogsFetchLock.current = false;
        setPublishLogsLoading(false);
      }
    },
    [notifyError],
  );

  const fetchHistoryPage = useCallback(
    async (offset) => {
      if (historyFetchLock.current) return;
      historyFetchLock.current = true;
      setHistoryLoading(true);
      try {
        const data = await getPricingHistory({ limit: HISTORY_LOG_PAGE_SIZE, offset });
        const rows = Array.isArray(data?.results) ? data.results : [];
        const hasMore = Boolean(data?.has_more);
        setHistory((prev) => (offset === 0 ? rows : [...prev, ...rows]));
        setHistoryHasMore(rows.length > 0 ? hasMore : false);
      } catch (err) {
        notifyError(err?.error || err?.message || 'Failed to load pricing history');
        if (offset === 0) setHistory([]);
        setHistoryHasMore(false);
      } finally {
        historyFetchLock.current = false;
        setHistoryLoading(false);
      }
    },
    [notifyError],
  );

  const SCROLL_LOAD_THROTTLE_MS = 450;

  const handlePublishLogsScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      if (!publishLogsHasMore || publishLogsLoading) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight >= 120) return;
      const now = Date.now();
      if (now - publishLogsScrollThrottleRef.current < SCROLL_LOAD_THROTTLE_MS) return;
      publishLogsScrollThrottleRef.current = now;
      void fetchPublishLogsPage(publishLogsLenRef.current);
    },
    [publishLogsHasMore, publishLogsLoading, fetchPublishLogsPage],
  );

  const handleHistoryScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      if (!historyHasMore || historyLoading) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight >= 120) return;
      const now = Date.now();
      if (now - historyScrollThrottleRef.current < SCROLL_LOAD_THROTTLE_MS) return;
      historyScrollThrottleRef.current = now;
      void fetchHistoryPage(historyLenRef.current);
    },
    [historyHasMore, historyLoading, fetchHistoryPage],
  );

  useEffect(() => {
    if (activeTab === 1) {
      fetchRoomTypes();
      fetchChannels();
      fetchRatePlans();
    }
  }, [activeTab, fetchRoomTypes, fetchChannels, fetchRatePlans]);

  useEffect(() => {
    if (activeTab !== 1) return undefined;
    const timer = window.setTimeout(() => {
      fetchRecommendations();
    }, 280);
    return () => window.clearTimeout(timer);
  }, [activeTab, fetchRecommendations, calendarMonth, selectedChannelId, allRoomTypesSelected, selectedRoomTypeIds]);

  useEffect(() => () => {
    if (recommendationsAbortRef.current) {
      recommendationsAbortRef.current.abort();
    }
  }, []);

  useEffect(() => {
    if (activeTab === 2) fetchRules();
  }, [activeTab, fetchRules]);

  useEffect(() => {
    if (activeTab === 3) fetchEvents();
  }, [activeTab, fetchEvents]);

  useEffect(() => {
    if (activeTab === 4) fetchCompetitor();
  }, [activeTab, fetchCompetitor]);

  useEffect(() => {
    if (activeTab === 6 && !roomTypes?.length) {
      fetchRoomTypes();
    }
  }, [activeTab, roomTypes?.length, fetchRoomTypes]);

  useEffect(() => {
    if (activeTab === 6) void fetchForecast();
  }, [activeTab, fetchForecast]);

  useEffect(() => {
    if (activeTab === 7) fetchConfig();
  }, [activeTab, fetchConfig]);

  useEffect(() => {
    if (activeTab !== 8) {
      prevActiveTabForLogsRef.current = activeTab;
      return;
    }
    const enteredTab7 = prevActiveTabForLogsRef.current !== 8;
    prevActiveTabForLogsRef.current = 8;
    if (!enteredTab7) return;
    historyLogsSubTabPrimedRef.current = false;
    setPublishLogs([]);
    setHistory([]);
    setPublishLogsHasMore(true);
    setHistoryHasMore(true);
    if (historyLogsTab === 0) {
      void fetchPublishLogsPage(0);
    }
  }, [activeTab, historyLogsTab, fetchPublishLogsPage]);

  useEffect(() => {
    if (historyLogsTab !== 1) {
      historyLogsSubTabPrimedRef.current = false;
    }
  }, [historyLogsTab]);

  useEffect(() => {
    if (activeTab !== 8 || historyLogsTab !== 1) return;
    if (historyLogsSubTabPrimedRef.current) return;
    historyLogsSubTabPrimedRef.current = true;
    void fetchHistoryPage(0);
  }, [activeTab, historyLogsTab, fetchHistoryPage]);

  /** If the first page is shorter than the viewport, scroll may not fire — chain loads until filled or has_more false. */
  useEffect(() => {
    if (activeTab !== 8 || historyLogsTab !== 0) return;
    if (publishLogsLoading || !publishLogsHasMore) return;
    const el = publishLogsScrollBoxRef.current;
    if (!el) return;
    const shortContent = el.scrollHeight <= el.clientHeight + 12;
    if (shortContent && publishLogsLenRef.current > 0) {
      void fetchPublishLogsPage(publishLogsLenRef.current);
    }
  }, [
    activeTab,
    historyLogsTab,
    publishLogs.length,
    publishLogsLoading,
    publishLogsHasMore,
    fetchPublishLogsPage,
  ]);

  useEffect(() => {
    if (activeTab !== 8 || historyLogsTab !== 1) return;
    if (historyLoading || !historyHasMore) return;
    const el = historyScrollBoxRef.current;
    if (!el) return;
    const shortContent = el.scrollHeight <= el.clientHeight + 12;
    if (shortContent && historyLenRef.current > 0) {
      void fetchHistoryPage(historyLenRef.current);
    }
  }, [activeTab, historyLogsTab, history.length, historyLoading, historyHasMore, fetchHistoryPage]);

  const handleSaveConfig = async () => {
    if (!config) return;
    const minM = config.default_min_multiplier;
    const maxM = config.default_max_multiplier;
    const minNum = minM === '' || minM == null || Number.isNaN(Number(minM)) ? 0.5 : Number(minM);
    const maxNum = maxM === '' || maxM == null || Number.isNaN(Number(maxM)) ? 2 : Number(maxM);
    if (minNum >= maxNum) {
      notifyWarning('Min multiplier must be less than max multiplier');
      return;
    }
    setLoading(true);
    try {
      const { created_at, updated_at, hotel, ...rest } = config;
      await updatePricingConfig({
        ...rest,
        occupancy_weight: Number(config.occupancy_weight),
        lead_time_weight: Number(config.lead_time_weight),
        velocity_weight: Number(config.velocity_weight),
        competitor_weight: Number(config.competitor_weight),
        default_min_multiplier: minNum,
        default_max_multiplier: maxNum,
        cron_interval_minutes:
          config.cron_interval_minutes === '' || config.cron_interval_minutes == null
            ? 0
            : Number(config.cron_interval_minutes),
      });
      setConfig((c) => ({
        ...c,
        default_min_multiplier: minNum,
        default_max_multiplier: maxNum,
      }));
    } catch (err) {
      notifyError(err?.error || 'Failed to save config');
    } finally {
      setLoading(false);
    }
  };

  if (routeInvalid) {
    return <Navigate to="/dynamic-pricing/" replace />;
  }

  return (
    <Box
      sx={{
        ...getDashboardPageShellSx(currentTheme),
        ...(isTabletLayout ? { p: { xs: 1.5, sm: 2 } } : {}),
        minHeight: '100vh',
      }}
    >
      <Box sx={{ mb: isTabletLayout ? 2 : 3 }}>
        <DashboardHeader
          title="Dynamic Pricing"
          breadcrumbs={[
            { label: 'Home', path: '/dashboard/' },
            { label: 'Dynamic Pricing' },
          ]}
          navLinks={[]}
        />
      </Box>

      <Paper elevation={0} sx={getDynamicPricingTabsPaperSx(isDark, isTabletLayout)}>
        <NotchTabs
          value={activeTab}
          onChange={handleMainTabChange}
          tabs={DYNAMIC_PRICING_TABS}
          accentColor={ACCENT_COLOR}
          sx={{ width: '100%', m: 0 }}
        />
      </Paper>

      <Paper elevation={0} sx={getDynamicPricingTabPanelSx(isDark, isTabletLayout)}>
      {activeTab === 0 && (
        <DynamicPricingDashboard
          isDark={isDark}
          accentColor={ACCENT_COLOR}
          themeColors={themeColors}
          notifyError={notifyError}
        />
      )}
          {activeTab === 1 && (
            <DynamicPricingRecommendationsTab
              isDark={isDark}
              accentColor={ACCENT_COLOR}
              themeColors={themeColors}
              recommendations={recommendations}
              loading={recommendationsLoading}
              recommendationsFetchError={recommendationsFetchError}
              calendarMonth={calendarMonth}
              onCalendarMonthChange={setCalendarMonth}
              channels={channels}
              selectedChannelId={selectedChannelId}
              onSelectedChannelIdChange={setSelectedChannelId}
              allRoomTypesSelected={allRoomTypesSelected}
              onAllRoomTypesSelectedChange={setAllRoomTypesSelected}
              selectedRoomTypeIds={selectedRoomTypeIds}
              onSelectedRoomTypeIdsChange={setSelectedRoomTypeIds}
              roomTypes={roomTypes}
              roomTypeMap={roomTypeMap}
              ratePlanMap={ratePlanMap}
              onRefresh={fetchRecommendations}
              onApplyRates={handleApply}
            />
          )}

          {activeTab === 2 && (
            <Box>
              <DynamicPricingTabHeader
                title="Pricing rules"
                description="Pricing rules: min floor, max ceiling, weekend markup, last-minute discount, occupancy surge."
                action={(
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => openRulesDrawer()}
                    sx={getDynamicPricingPrimaryButtonSx(ACCENT_COLOR)}
                  >
                    Add Rule
                  </Button>
                )}
              />
              {rules.length === 0 && !loading && (
                <Typography color="text.secondary">No rules. Click &quot;Add Rule&quot; to create one.</Typography>
              )}
              <Grid container spacing={2}>
                {rules.map((r) => {
                  const typeLabel = RULE_TYPES.find((t) => t.value === r.rule_type)?.label || r.rule_type;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={r.id}>
                      <Card elevation={0} sx={getDynamicPricingMetricCardSx(isDark, ACCENT_COLOR)}>
                        <CardContent sx={{ flex: 1, p: 2, pt: 2.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {r.name}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton size="small" onClick={() => openRulesDrawer(r)} aria-label="Edit rule">
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => setRuleDeleteConfirm(r)} aria-label="Delete rule">
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {typeLabel}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                            <Typography variant="h6" component="span" sx={getDynamicPricingValueTypographySx(isDark, ACCENT_COLOR)}>
                              {r.value != null ? Number(r.value) : '—'}
                            </Typography>
                            <Chip
                              size="small"
                              label={r.is_active ? 'Active' : 'Off'}
                              sx={r.is_active ? { bgcolor: `${ACCENT_COLOR}22`, color: ACCENT_COLOR, fontWeight: 700 } : undefined}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Add / Edit Rule drawer – CheckIn-style header & footer */}
              <Drawer
                anchor="right"
                open={rulesDrawerOpen}
                onClose={closeRulesDrawer}
                sx={getDrawerRootSx()}
                PaperProps={{
                  sx: {
                    width: { xs: '100%', sm: 440 },
                    backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      borderBottom: `2px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                      backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tune sx={{ color: ACCENT_COLOR, fontSize: 24 }} />
                      <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                        {editingRuleId ? 'Edit Rule' : 'Add Rule'}
                      </Typography>
                    </Box>
                    <IconButton onClick={closeRulesDrawer} sx={{ color: isDark ? '#999' : '#666' }} aria-label="Close">
                      <Close />
                    </IconButton>
                  </Box>
                  {/* Content */}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={ruleFormData.name}
                      onChange={(e) => setRuleFormData((prev) => ({ ...prev, name: e.target.value }))}
                      sx={DRAWER_INPUT_SX}
                      size="medium"
                    />
                    <FormControl fullWidth size="medium" sx={DRAWER_INPUT_SX}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        label="Type"
                        value={ruleFormData.rule_type}
                        MenuProps={drawerSelectMenuProps}
                        onChange={(e) => setRuleFormData((prev) => ({ ...prev, rule_type: e.target.value }))}
                      >
                        {RULE_TYPES.map((t) => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Value"
                      type="number"
                      value={ruleFormData.value}
                      onChange={(e) => setRuleFormData((prev) => ({ ...prev, value: e.target.value }))}
                      inputProps={{ step: 0.01, min: 0 }}
                      sx={DRAWER_INPUT_SX}
                      size="medium"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={ruleFormData.is_active}
                          onChange={(e) => setRuleFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                          color="primary"
                        />
                      }
                      label="Active"
                    />
                  </Box>
                  {/* Footer */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderTop: `2px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                      backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                    }}
                  >
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={closeRulesDrawer}
                          disabled={savingRule}
                          sx={{
                            borderColor: isDark ? '#333246' : '#e0e0e0',
                            color: isDark ? '#ddd' : '#666',
                            '&:hover': {
                              borderColor: ACCENT_COLOR,
                              backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                              color: ACCENT_COLOR,
                            },
                          }}
                        >
                          Cancel
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={saveRule}
                          disabled={savingRule}
                          sx={{
                            bgcolor: ACCENT_COLOR,
                            color: '#000',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#3bc9a8' },
                          }}
                        >
                          {savingRule ? 'Saving…' : 'Save'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Drawer>

              {/* Delete rule confirm dialog */}
              <Dialog open={!!ruleDeleteConfirm} onClose={() => setRuleDeleteConfirm(null)}>
                <DialogTitle>Delete rule?</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    {ruleDeleteConfirm && `Delete rule "${ruleDeleteConfirm.name}"? This cannot be undone.`}
                  </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                  <Button onClick={() => setRuleDeleteConfirm(null)}>Cancel</Button>
                  <Button color="error" variant="contained" onClick={() => ruleDeleteConfirm && handleDeleteRule(ruleDeleteConfirm.id)}>
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          )}

          {activeTab === 3 && (
            <Box>
              <DynamicPricingTabHeader
                title="Special events"
                description="Special events with price multipliers (e.g. festival +20%)."
                action={(
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => openEventsDrawer()}
                    sx={getDynamicPricingPrimaryButtonSx(ACCENT_COLOR)}
                  >
                    Add Event
                  </Button>
                )}
              />
              {events.length === 0 && !loading && (
                <Typography color="text.secondary">No events. Click &quot;Add Event&quot; to create one.</Typography>
              )}
              <Grid container spacing={2}>
                {events.map((e) => (
                  <Grid item xs={12} sm={6} md={4} key={e.id}>
                    <Card elevation={0} sx={getDynamicPricingMetricCardSx(isDark, ACCENT_COLOR)}>
                      <CardContent sx={{ flex: 1, p: 2, pt: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {e.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" onClick={() => openEventsDrawer(e)} aria-label="Edit event">
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => setEventDeleteConfirm(e)} aria-label="Delete event">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Start: {e.start_date}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          End: {e.end_date}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                          <Typography variant="h6" component="span" sx={getDynamicPricingValueTypographySx(isDark, ACCENT_COLOR)}>
                            ×{e.price_multiplier != null ? Number(e.price_multiplier) : '1'}
                          </Typography>
                          <Chip
                            size="small"
                            label={e.is_active ? 'Active' : 'Off'}
                            sx={e.is_active ? { bgcolor: `${ACCENT_COLOR}22`, color: ACCENT_COLOR, fontWeight: 700 } : undefined}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Add / Edit Event drawer – same design as Rules */}
              <Drawer
                anchor="right"
                open={eventsDrawerOpen}
                onClose={closeEventsDrawer}
                sx={getDrawerRootSx()}
                PaperProps={{
                  sx: {
                    width: { xs: '100%', sm: 440 },
                    backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                  },
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      borderBottom: `2px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                      backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Event sx={{ color: ACCENT_COLOR, fontSize: 24 }} />
                      <Typography variant="h6" sx={{ color: isDark ? '#ddd' : '#333', fontWeight: 600 }}>
                        {editingEventId ? 'Edit Event' : 'Add Event'}
                      </Typography>
                    </Box>
                    <IconButton onClick={closeEventsDrawer} sx={{ color: isDark ? '#999' : '#666' }} aria-label="Close">
                      <Close />
                    </IconButton>
                  </Box>
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={eventFormData.name}
                      onChange={(e) => setEventFormData((prev) => ({ ...prev, name: e.target.value }))}
                      sx={DRAWER_INPUT_SX}
                      size="medium"
                    />
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        label="Start date"
                        value={eventFormData.start_date ? moment(eventFormData.start_date) : null}
                        onChange={(date) => setEventFormData((prev) => ({ ...prev, start_date: date ? moment(date).format('YYYY-MM-DD') : '' }))}
                        slotProps={{
                          ...drawerDatePickerSlotProps,
                          textField: {
                            fullWidth: true,
                            sx: DRAWER_INPUT_SX,
                            size: 'medium',
                          },
                        }}
                      />
                      <DatePicker
                        label="End date"
                        value={eventFormData.end_date ? moment(eventFormData.end_date) : null}
                        onChange={(date) => setEventFormData((prev) => ({ ...prev, end_date: date ? moment(date).format('YYYY-MM-DD') : '' }))}
                        slotProps={{
                          ...drawerDatePickerSlotProps,
                          textField: {
                            fullWidth: true,
                            sx: DRAWER_INPUT_SX,
                            size: 'medium',
                          },
                        }}
                      />
                    </LocalizationProvider>
                    <TextField
                      fullWidth
                      label="Price multiplier"
                      type="number"
                      value={eventFormData.price_multiplier}
                      onChange={(e) => setEventFormData((prev) => ({ ...prev, price_multiplier: e.target.value }))}
                      inputProps={{ step: 0.01, min: 0.01 }}
                      helperText="e.g. 1.2 = +20%"
                      sx={DRAWER_INPUT_SX}
                      size="medium"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={eventFormData.is_active}
                          onChange={(e) => setEventFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
                          color="primary"
                        />
                      }
                      label="Active"
                    />
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      borderTop: `2px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                      backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                    }}
                  >
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={closeEventsDrawer}
                          disabled={savingEvent}
                          sx={{
                            borderColor: isDark ? '#333246' : '#e0e0e0',
                            color: isDark ? '#ddd' : '#666',
                            '&:hover': {
                              borderColor: ACCENT_COLOR,
                              backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                              color: ACCENT_COLOR,
                            },
                          }}
                        >
                          Cancel
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={saveEvent}
                          disabled={savingEvent}
                          sx={{
                            bgcolor: ACCENT_COLOR,
                            color: '#000',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#3bc9a8' },
                          }}
                        >
                          {savingEvent ? 'Saving…' : 'Save'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Drawer>

              <Dialog open={!!eventDeleteConfirm} onClose={() => setEventDeleteConfirm(null)}>
                <DialogTitle>Delete event?</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    {eventDeleteConfirm && `Delete event "${eventDeleteConfirm.name}"? This cannot be undone.`}
                  </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                  <Button onClick={() => setEventDeleteConfirm(null)}>Cancel</Button>
                  <Button color="error" variant="contained" onClick={() => eventDeleteConfirm && handleDeleteEvent(eventDeleteConfirm.id)}>
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          )}

          {activeTab === 4 && (
            <Box>
              <DynamicPricingTabHeader
                title="Competitor intelligence"
                description="Compare nearby hotels, sync competitor rates, and map your market position."
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  columnGap: 1,
                  rowGap: 0,
                  mb: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Tabs
                  value={competitorSubTab}
                  onChange={handleCompetitorSubTabChange}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderBottom: 'none',
                    mb: 0,
                    ...getDynamicPricingAccentTabsSx(ACCENT_COLOR),
                  }}
                >
                  <Tab key="competitor-compare" id="competitor-subtab-compare" icon={<CompareArrows fontSize="small" />} iconPosition="start" label="Price comparison" />
                  <Tab key="competitor-rates" id="competitor-subtab-rates" icon={<RateIcon fontSize="small" />} iconPosition="start" label="Competitor rate" />
                  <Tab key="competitor-map" id="competitor-subtab-map" icon={<MapIcon fontSize="small" />} iconPosition="start" label="Competitor map" />
                </Tabs>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', flexShrink: 0, pb: 0.25 }}>
                  <Button
                    variant="contained"
                    startIcon={<Sync />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setCompetitorSyncMenuAnchor(e.currentTarget)}
                    disabled={
                      importLoading ||
                      refreshExistingRatesLoading ||
                      refreshExistingHotelInfoLoading ||
                      fullPipelineLoading
                    }
                    sx={{
                      backgroundColor: ACCENT_COLOR,
                      color: '#000',
                      fontWeight: 600,
                      minWidth: 140,
                      height: '36px',
                      '&:hover': {
                        backgroundColor: ACCENT_COLOR,
                        opacity: 0.9,
                      },
                    }}
                  >
                    Sync
                  </Button>
                  <Menu
                    anchorEl={competitorSyncMenuAnchor}
                    open={Boolean(competitorSyncMenuAnchor)}
                    onClose={() => setCompetitorSyncMenuAnchor(null)}
                    PaperProps={{
                      sx: {
                        backgroundColor: isDark ? '#2a2a31' : '#ffffff',
                        backgroundImage: isDark
                          ? 'linear-gradient(rgb(104 107 118 / 16%), rgb(22 22 25 / 0%))'
                          : 'none',
                        border: `1px solid ${isDark ? '#333246' : '#e0e0e0'}`,
                        minWidth: 288,
                        mt: 1,
                      },
                    }}
                  >
                    <MenuItem
                      disabled={importLoading || fullPipelineLoading}
                      onClick={() => {
                        setCompetitorSyncMenuAnchor(null);
                        handleCompetitorRefresh();
                      }}
                      sx={{
                        color: isDark ? '#ddd' : '#333',
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        {importLoading ? <CircularProgress size={20} color="inherit" /> : <Refresh sx={{ color: ACCENT_COLOR }} />}
                      </ListItemIcon>
                      <ListItemText
                        primary={importLoading ? 'Discovering…' : 'Discover nearby hotels'}
                        secondary="Places / OSM only — no rates or profile sync"
                        primaryTypographyProps={{ fontWeight: 700 }}
                        secondaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.85 } }}
                      />
                    </MenuItem>
                    <MenuItem
                      disabled={fullPipelineLoading}
                      onClick={() => {
                        setCompetitorSyncMenuAnchor(null);
                        handleDiscoverAndFullSync();
                      }}
                      sx={{
                        color: isDark ? '#ddd' : '#333',
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        {fullPipelineLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <TravelExplore sx={{ color: ACCENT_COLOR }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={fullPipelineLoading ? 'Full pipeline running…' : 'Full pipeline'}
                        secondary="Discover, then all rates, then all profiles"
                        primaryTypographyProps={{ fontWeight: 700 }}
                        secondaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.85 } }}
                      />
                    </MenuItem>
                    <MenuItem
                      disabled={refreshExistingRatesLoading || fullPipelineLoading}
                      onClick={() => {
                        setCompetitorSyncMenuAnchor(null);
                        handleBulkSyncCompetitorRates();
                      }}
                      sx={{
                        color: isDark ? '#ddd' : '#333',
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        {refreshExistingRatesLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <MonetizationOn sx={{ color: ACCENT_COLOR }} />
                        )}
                      </ListItemIcon>
                      <ListItemText>{refreshExistingRatesLoading ? 'Syncing rates…' : 'All rates'}</ListItemText>
                    </MenuItem>
                    <MenuItem
                      disabled={refreshExistingHotelInfoLoading || fullPipelineLoading}
                      onClick={() => {
                        setCompetitorSyncMenuAnchor(null);
                        handleBulkSyncCompetitorHotelInfo();
                      }}
                      sx={{
                        color: isDark ? '#ddd' : '#333',
                        '&:hover': {
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        {refreshExistingHotelInfoLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <Apartment sx={{ color: ACCENT_COLOR }} />
                        )}
                      </ListItemIcon>
                      <ListItemText>
                        {refreshExistingHotelInfoLoading ? 'Syncing profiles…' : 'All hotel info'}
                      </ListItemText>
                    </MenuItem>
                  </Menu>
                  <Button variant="contained" startIcon={<Add />} onClick={openAddCompetitorDrawer} sx={{ bgcolor: ACCENT_COLOR, color: '#111', fontWeight: 600, height: '36px', '&:hover': { bgcolor: '#3bc9a8' } }}>
                    Add Competitor
                  </Button>
                </Box>
              </Box>

              {competitorSubTab === 0 && (
                <>
              {/* ——— Price comparison: Your hotel vs competitors (OTA-style) ——— */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Your hotel card */}
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Card
                    elevation={0}
                    sx={{
                      ...getDynamicPricingMetricCardSx(isDark, ACCENT_COLOR),
                      border: `2px solid ${ACCENT_COLOR}`,
                    }}
                  >
                    <CardContent sx={{ position: 'relative', pt: 2 }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          zIndex: 2,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Tooltip title="Sync your hotel nightly rate via AI (hotel currency)">
                          <span>
                            <IconButton
                              size="small"
                              onClick={handleSyncOurHotelRate}
                              disabled={ourHotelProfileSyncLoading || ourHotelRateSyncLoading}
                              aria-label="Sync your hotel rate via AI"
                            >
                              {ourHotelRateSyncLoading ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <MonetizationOn fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Sync hotel profile from Google Places — rating, photo, reviews, amenities">
                          <span>
                            <IconButton
                              size="small"
                              onClick={handleSyncOurHotelProfile}
                              disabled={ourHotelProfileSyncLoading || ourHotelRateSyncLoading}
                              aria-label="Sync your hotel profile from Places"
                            >
                              {ourHotelProfileSyncLoading ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <Apartment fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      {(ourHotelProfileSyncLoading || ourHotelRateSyncLoading) ? (
                        <PriceCompareCardSkeleton />
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                            <Avatar
                              src={ourHotelGoogle?.primary_photo_url || undefined}
                              alt=""
                              sx={{
                                width: 64,
                                height: 64,
                                flexShrink: 0,
                                border: '2px solid',
                                borderColor: ACCENT_COLOR,
                                bgcolor: isDark ? 'grey.800' : 'grey.200',
                              }}
                            >
                              {!ourHotelGoogle?.primary_photo_url && <Hotel sx={{ color: 'text.secondary' }} />}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0, pr: 5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
                                <Star sx={{ color: ACCENT_COLOR, fontSize: 20 }} />
                                <Chip label="Your hotel" size="small" sx={{ bgcolor: ACCENT_COLOR, color: '#0d1f1a', fontWeight: 600 }} />
                              </Box>
                              <Typography variant="subtitle1" fontWeight={700} noWrap title={typeof window !== 'undefined' ? window.DJANGO_CONTEXT?.selectedHotel?.name : ''}>
                                {typeof window !== 'undefined' && window.DJANGO_CONTEXT?.selectedHotel?.name
                                  ? window.DJANGO_CONTEXT.selectedHotel.name
                                  : 'Your property'}
                              </Typography>
                              {(typeof window !== 'undefined' && window.DJANGO_CONTEXT?.selectedHotel?.city) && (
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {[window.DJANGO_CONTEXT.selectedHotel.city, window.DJANGO_CONTEXT.selectedHotel.country].filter(Boolean).join(', ')}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary' }}>
                            {ourHotelGoogle?.google_rate != null
                              ? `₹${Math.round(ourHotelGoogle.google_rate).toLocaleString('en-IN')}`
                              : ourReferenceRate != null
                                ? `₹${Math.round(ourReferenceRate).toLocaleString('en-IN')}`
                                : '—'}
                          </Typography>
                          {(ourHotelGoogle?.rating != null || ourHotelGoogle?.user_rating_count != null) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <Star sx={{ color: 'warning.main', fontSize: 18 }} />
                              <Typography variant="body2" fontWeight={600}>
                                {ourHotelGoogle?.rating != null ? Number(ourHotelGoogle.rating).toFixed(1) : '—'}
                              </Typography>
                              {ourHotelGoogle?.user_rating_count != null && (
                                <Typography variant="caption" color="text.secondary">({Number(ourHotelGoogle.user_rating_count).toLocaleString()} reviews)</Typography>
                              )}
                            </Box>
                          )}
                          {Array.isArray(ourHotelGoogle?.reviews) && ourHotelGoogle.reviews.length > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
                              noWrap
                              title={ourHotelGoogle.reviews[0]?.text ?? ''}
                            >
                              "{(ourHotelGoogle.reviews[0]?.text ?? '').toString().slice(0, 60)}…"
                            </Typography>
                          )}
                          {Array.isArray(ourHotelGoogle?.amenities) && ourHotelGoogle.amenities.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 1 }}>
                              {ourHotelGoogle.amenities.slice(0, 6).map((a, i) => (
                                <Chip key={i} label={String(a).replace(/_/g, ' ')} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                              ))}
                            </Box>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {ourHotelGoogle?.google_rate != null
                              ? `Google Hotel · ${ourHotelGoogle.fetched_at ? new Date(ourHotelGoogle.fetched_at).toLocaleString() : ''}`
                              : ourReferenceRate != null
                                ? 'Avg from Recommendations'
                                : 'Fetch from Google or load Recommendations'}
                          </Typography>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                {/* Competitor cards */}
                {competitorHotels.map((c, idx) => {
                  const compRate = c.average_rate != null && c.average_rate !== '' ? Number(c.average_rate) : null;
                  const currency = (c.currency || 'INR').toUpperCase();
                  const symbol = currency === 'INR' ? '₹' : currency === 'USD' ? '$' : currency;
                  const ourBaseline = ourHotelGoogle?.google_rate ?? ourReferenceRate;
                  const vsOurs =
                    ourBaseline != null && compRate != null && ourBaseline > 0
                      ? ((compRate - ourBaseline) / ourBaseline) * 100
                      : null;
                  const compSyncing = syncingCompetitorId != null && c.id != null && Number(syncingCompetitorId) === Number(c.id);
                  const compRateSyncing = rateSyncingCompetitorId != null && c.id != null && Number(rateSyncingCompetitorId) === Number(c.id);
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={c.id ?? `ch-${idx}`}>
                      <Card
                        elevation={0}
                        sx={{
                          ...getDynamicPricingMetricCardSx(isDark, ACCENT_COLOR),
                          '&:hover': { boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.35)' : '0 8px 20px rgba(15, 23, 42, 0.08)' },
                        }}
                      >
                        <CardContent sx={{ position: 'relative', pt: 2, pb: 5 }}>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              zIndex: 2,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <Tooltip title="Sync today’s rate via AI (hotel currency)">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={compSyncing || compRateSyncing || c.id == null}
                                  onClick={() => handleSyncCompetitorRateOnly(c.id)}
                                  aria-label="Sync competitor rate via AI"
                                >
                                  {compRateSyncing ? (
                                    <CircularProgress size={20} color="inherit" />
                                  ) : (
                                    <MonetizationOn fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Sync hotel profile from Google Places — address, photo, reviews, amenities (no nightly rate)">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={compSyncing || compRateSyncing || c.id == null}
                                  onClick={() => handleSyncCompetitor(c.id)}
                                  aria-label="Sync competitor hotel info from Google"
                                >
                                  {compSyncing ? (
                                    <CircularProgress size={20} color="inherit" />
                                  ) : (
                                    <Apartment fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                          {(compSyncing || compRateSyncing) ? (
                            <PriceCompareCardSkeleton />
                          ) : (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                                <Avatar
                                  src={c.primary_photo_url || undefined}
                                  alt=""
                                  sx={{
                                    width: 64,
                                    height: 64,
                                    flexShrink: 0,
                                    bgcolor: isDark ? 'grey.800' : 'grey.200',
                                    border: '1px solid',
                                    borderColor: isDark ? 'divider' : 'grey.300',
                                  }}
                                >
                                  {c.name ? String(c.name).charAt(0).toUpperCase() : <Hotel sx={{ color: 'text.secondary', fontSize: 28 }} />}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0, pr: 5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
                                    <Hotel fontSize="small" color="action" />
                                    <Chip label="Competitor" size="small" variant="outlined" />
                                  </Box>
                                  <Typography variant="subtitle1" fontWeight={700} noWrap title={c.name ?? ''}>
                                    {c.name ?? '—'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: '100%' }} title={c.address ?? ''}>
                                    {c.address?.trim() ? c.address : '—'}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary' }}>
                                  {compRate != null ? `${symbol}${Number(compRate).toLocaleString('en-IN')}` : '—'}
                                </Typography>
                                {vsOurs != null && (
                                  <Chip
                                    size="small"
                                    icon={vsOurs < 0 ? <TrendingDown /> : vsOurs > 0 ? <TrendingUp /> : <Remove />}
                                    label={vsOurs < 0 ? `${Math.abs(vsOurs).toFixed(0)}% lower` : vsOurs > 0 ? `${vsOurs.toFixed(0)}% higher` : 'Same'}
                                    sx={{
                                      flexShrink: 0,
                                      ml: 'auto',
                                      bgcolor: vsOurs < 0 ? (isDark ? 'success.dark' : 'success.light') : vsOurs > 0 ? (isDark ? 'warning.dark' : 'warning.light') : 'action.hover',
                                      color: vsOurs === 0 ? 'text.secondary' : 'text.primary',
                                    }}
                                  />
                                )}
                              </Box>
                              {(c.rating != null || c.user_rating_count != null) && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                  <Star sx={{ color: 'warning.main', fontSize: 18 }} />
                                  <Typography variant="body2" fontWeight={600}>
                                    {c.rating != null ? Number(c.rating).toFixed(1) : '—'}
                                  </Typography>
                                  {c.user_rating_count != null && (
                                    <Typography variant="caption" color="text.secondary">({Number(c.user_rating_count).toLocaleString()} reviews)</Typography>
                                  )}
                                </Box>
                              )}
                              {c.reviews?.length > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }} noWrap title={c.reviews[0].text}>
                                  "{typeof c.reviews[0].text === 'string' ? c.reviews[0].text.slice(0, 60) : ''}…"
                                </Typography>
                              )}
                              {c.amenities?.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 1 }}>
                                  {c.amenities.slice(0, 6).map((a, i) => (
                                    <Chip key={i} label={String(a).replace(/_/g, ' ')} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                  ))}
                                </Box>
                              )}
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {c.last_rate_fetched_at
                                  ? `Google Hotel · ${new Date(c.last_rate_fetched_at).toLocaleString()}`
                                  : (c.source ?? 'OTA').replace(/_/g, ' ')}
                              </Typography>
                            </>
                          )}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 6,
                              right: 6,
                              zIndex: 2,
                            }}
                          >
                            <Tooltip title="Remove competitor and all stored rates">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={
                                    compSyncing ||
                                    compRateSyncing ||
                                    c.id == null ||
                                    (deletingCompetitorId != null && Number(deletingCompetitorId) === Number(c.id))
                                  }
                                  onClick={() => c.id != null && setCompetitorDeleteConfirm({ id: c.id, name: c.name || 'Competitor' })}
                                  aria-label="Delete competitor"
                                >
                                  {deletingCompetitorId != null && Number(deletingCompetitorId) === Number(c.id) ? (
                                    <CircularProgress size={18} color="inherit" />
                                  ) : (
                                    <Delete fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
                </>
              )}

              {competitorSubTab === 1 && (
                <>
              <Card variant="outlined" sx={{ borderColor: 'divider', overflow: 'hidden' }}>
                <CardContent sx={{ pt: 2, pb: 2, px: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    All competitors with average rate vs your baseline. Use the row actions: rate sync (daily) or full hotel profile (monthly). Screenshots open from the image icon.
                  </Typography>
                  <DataTable
                columns={[
                  {
                    field: 'name',
                    label: 'Hotel',
                    sortable: true,
                    render: (_, row) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={row.primary_photo_url || undefined}
                          alt=""
                          sx={{
                            width: 44,
                            height: 44,
                            flexShrink: 0,
                            bgcolor: isDark ? 'grey.800' : 'grey.200',
                            border: '1px solid',
                            borderColor: isDark ? 'divider' : 'grey.300',
                          }}
                        >
                          {row.name ? String(row.name).charAt(0).toUpperCase() : <Hotel sx={{ fontSize: 22, color: 'text.secondary' }} />}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 200 }} title={row.name ?? ''}>
                          {row.name ?? '—'}
                        </Typography>
                      </Box>
                    ),
                  },
                  {
                    field: 'address',
                    label: 'Address',
                    sortable: false,
                    render: (_, row) => (
                      <Typography variant="body2" noWrap sx={{ maxWidth: 260 }} title={row.address || ''}>
                        {row.address?.trim() ? row.address : '—'}
                      </Typography>
                    ),
                  },
                  {
                    field: 'rating',
                    label: 'Rating',
                    sortable: true,
                    render: (_, row) =>
                      row.rating != null || row.user_rating_count != null ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star sx={{ color: 'warning.main', fontSize: 18 }} />
                          <Typography variant="body2" fontWeight={600} component="span">
                            {row.rating != null ? Number(row.rating).toFixed(1) : '—'}
                          </Typography>
                          {row.user_rating_count != null && (
                            <Typography variant="caption" color="text.secondary" component="span">
                              ({Number(row.user_rating_count).toLocaleString()})
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        '—'
                      ),
                  },
                  {
                    field: 'average_rate',
                    label: 'Price',
                    sortable: true,
                    align: 'right',
                    render: (_, row) => {
                      const compRate = row.average_rate != null && row.average_rate !== '' ? Number(row.average_rate) : null;
                      const currency = (row.currency || 'INR').toUpperCase();
                      const symbol = currency === 'INR' ? '₹' : currency === 'USD' ? '$' : currency;
                      const ourBaseline = ourHotelGoogle?.google_rate ?? ourReferenceRate;
                      const vsOurs =
                        ourBaseline != null && compRate != null && ourBaseline > 0
                          ? ((compRate - ourBaseline) / ourBaseline) * 100
                          : null;
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography sx={{ color: ACCENT_COLOR, fontWeight: 700 }}>
                            {compRate != null ? `${symbol}${Number(compRate).toLocaleString('en-IN')}` : '—'}
                          </Typography>
                          {vsOurs != null && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={vsOurs < 0 ? `${Math.abs(vsOurs).toFixed(1)}% lower` : vsOurs > 0 ? `${vsOurs.toFixed(1)}% higher` : 'Same'}
                              sx={{
                                flexShrink: 0,
                                borderColor: vsOurs < 0 ? 'success.main' : vsOurs > 0 ? 'warning.main' : 'grey.500',
                                color: vsOurs < 0 ? 'success.main' : vsOurs > 0 ? 'warning.dark' : 'text.secondary',
                              }}
                            />
                          )}
                        </Box>
                      );
                    },
                  },
                  {
                    field: 'last_rate_fetched_at',
                    label: 'Last sync',
                    sortable: false,
                    render: (_, row) => {
                      const d = row.last_rate_fetched_at;
                      if (!d) return '—';
                      const dt = new Date(d);
                      return Number.isNaN(dt.getTime()) ? '—' : `Google Hotel · ${dt.toLocaleString()}`;
                    },
                  },
                  {
                    field: 'source',
                    label: 'Source',
                    sortable: false,
                    render: (_, row) => (row.source ?? '—').replace(/_/g, ' '),
                  },
                  {
                    field: 'sync_actions',
                    label: 'Action',
                    sortable: false,
                    align: 'right',
                    render: (_, row) => {
                      const rid = row.id;
                      const rateBusy = rateSyncingCompetitorId != null && rid != null && Number(rateSyncingCompetitorId) === Number(rid);
                      const fullBusy = syncingCompetitorId != null && rid != null && Number(syncingCompetitorId) === Number(rid);
                      const delBusy = deletingCompetitorId != null && rid != null && Number(deletingCompetitorId) === Number(rid);
                      return (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
                          <Tooltip title="Sync today’s rate via AI">
                            <span>
                              <IconButton
                                size="small"
                                disabled={rid == null || rateBusy || fullBusy || delBusy}
                                onClick={() => handleSyncCompetitorRateOnly(rid)}
                                aria-label="Sync competitor rate"
                              >
                                {rateBusy ? <CircularProgress size={18} color="inherit" /> : <MonetizationOn fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Sync profile from Google Places (no nightly rate)">
                            <span>
                              <IconButton
                                size="small"
                                disabled={rid == null || rateBusy || fullBusy || delBusy}
                                onClick={() => handleSyncCompetitor(rid)}
                                aria-label="Sync competitor hotel info"
                              >
                                {fullBusy ? <CircularProgress size={18} color="inherit" /> : <Apartment fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Open Competitor map and focus this row">
                            <span>
                              <IconButton
                                size="small"
                                disabled={rid == null || rateBusy || fullBusy || delBusy}
                                onClick={() => openCompetitorMapForRow(rid)}
                                aria-label="Show on map"
                              >
                                <MapIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Remove competitor and all stored rates">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={rid == null || rateBusy || fullBusy || delBusy}
                                onClick={() => rid != null && setCompetitorDeleteConfirm({ id: rid, name: row.name || 'Competitor' })}
                                aria-label="Delete competitor"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      );
                    },
                  },
                ]}
                data={competitorListData}
                loading={competitorListLoading}
                pagination={competitorListPagination}
                onPageChange={handleCompetitorListPageChange}
                onPerPageChange={handleCompetitorListPerPageChange}
                onSortChange={handleCompetitorListSortChange}
                sortConfig={competitorListSortConfig}
                emptyMessage="No competitors yet. Use Refresh (discover & import) or Add Competitor."
                stickyHeader={true}
                  />
                </CardContent>
              </Card>
                </>
              )}

              {competitorSubTab === 2 && (
                <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Price markers use a <strong>Google-style pill</strong> (white badge, amount in bold, hotel icon): teal disc + house = your hotel, pink disc + bed = competitors. Gradient frame around the map. Tap a pin for details; chips below jump or open Google Maps.
              </Typography>
              <CompetitorLocationMap
                propertyHotel={typeof window !== 'undefined' ? window.DJANGO_CONTEXT?.selectedHotel : null}
                propertyRate={ourHotelGoogle?.google_rate}
                propertyCurrency={
                  typeof window !== 'undefined'
                    ? (window.DJANGO_CONTEXT?.selectedHotel?.currency || 'INR')
                    : 'INR'
                }
                competitors={competitorHotels}
                accentColor={ACCENT_COLOR}
                isDark={isDark}
                focusCompetitorId={mapFocusCompetitorId}
                onFocusApplied={() => setMapFocusCompetitorId(null)}
              />
                </>
              )}

              <Dialog
                open={!!competitorDeleteConfirm}
                onClose={() => {
                  if (!deletingCompetitorId) setCompetitorDeleteConfirm(null);
                }}
              >
                <DialogTitle>Delete competitor?</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    {competitorDeleteConfirm &&
                      `Remove "${competitorDeleteConfirm.name}" from your list and delete all stored nightly rates for this competitor. This cannot be undone.`}
                  </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                  <Button onClick={() => setCompetitorDeleteConfirm(null)} disabled={deletingCompetitorId != null}>
                    Cancel
                  </Button>
                  <Button
                    color="error"
                    variant="contained"
                    disabled={deletingCompetitorId != null}
                    onClick={() => competitorDeleteConfirm && handleDeleteCompetitor(competitorDeleteConfirm.id)}
                  >
                    {deletingCompetitorId != null ? <CircularProgress size={22} color="inherit" /> : 'Delete'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Add Competitor drawer (same width + theme as check-in drawer) */}
              <Drawer
                anchor="right"
                open={addCompetitorDrawerOpen}
                onClose={closeAddCompetitorDrawer}
                PaperProps={{
                  sx: getBookingDrawerPaperSx(isDark, {
                    width: { xs: '100%', sm: 600 },
                  }),
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      ...getBookingDrawerHeaderSx(isDark),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Hotel sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 24 }} />
                      <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600 }}>
                        Add Competitor
                      </Typography>
                    </Box>
                    <IconButton
                      onClick={closeAddCompetitorDrawer}
                      size="small"
                      sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
                      aria-label="Close"
                    >
                      <Close />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      overflowY: 'auto',
                      p: 1.5,
                      ...getGuestDrawerContentSx(isDark),
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Hotel name"
                      value={addCompetitorForm.name}
                      onChange={(e) => setAddCompetitorForm((p) => ({ ...p, name: e.target.value }))}
                      sx={{ mb: 2, ...getGuestDrawerFieldSx(isDark) }}
                    />
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleSearchCompetitorByName}
                      disabled={searchingCompetitorByName || !(addCompetitorForm.name || '').trim()}
                      startIcon={searchingCompetitorByName ? <CircularProgress size={16} /> : <Refresh />}
                      sx={{
                        mb: 2,
                        textTransform: 'none',
                        ...getBookingDrawerOutlinedButtonSx(isDark),
                        borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
                        color: BOOKING_DRAWER_ACCENT,
                        '&:hover': {
                          borderColor: BOOKING_DRAWER_ACCENT,
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                        },
                      }}
                    >
                      {searchingCompetitorByName ? 'Searching…' : 'Search & autofill from Google'}
                    </Button>

                    {addCompetitorForm.primary_photo_url && (
                      <Box
                        sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          mb: 2,
                          aspectRatio: '16/10',
                          bgcolor: isDark ? drawerTheme.chipBg : 'grey.200',
                          border: `1px solid ${drawerTheme.surfaceBorder}`,
                        }}
                      >
                        <img src={addCompetitorForm.primary_photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    )}
                    <TextField
                      fullWidth
                      label="Address"
                      value={addCompetitorForm.address}
                      onChange={(e) => setAddCompetitorForm((p) => ({ ...p, address: e.target.value }))}
                      sx={{ mb: 2, ...getGuestDrawerFieldSx(isDark) }}
                    />
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Avg rate"
                          type="number"
                          value={addCompetitorForm.average_rate}
                          onChange={(e) => setAddCompetitorForm((p) => ({ ...p, average_rate: e.target.value }))}
                          sx={{ mb: 2, ...getGuestDrawerFieldSx(isDark) }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Currency"
                          value={addCompetitorForm.currency}
                          onChange={(e) => setAddCompetitorForm((p) => ({ ...p, currency: e.target.value }))}
                          sx={{ mb: 2, ...getGuestDrawerFieldSx(isDark) }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Rating"
                          type="number"
                          inputProps={{ step: 0.1, min: 0, max: 5 }}
                          value={addCompetitorForm.rating}
                          onChange={(e) => setAddCompetitorForm((p) => ({ ...p, rating: e.target.value }))}
                          sx={{ mb: 2, ...getGuestDrawerFieldSx(isDark) }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Reviews count"
                          type="number"
                          value={addCompetitorForm.user_rating_count}
                          onChange={(e) => setAddCompetitorForm((p) => ({ ...p, user_rating_count: e.target.value }))}
                          sx={{ mb: 2, ...getGuestDrawerFieldSx(isDark) }}
                        />
                      </Grid>
                    </Grid>
                    {Array.isArray(addCompetitorForm.amenities) && addCompetitorForm.amenities.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {addCompetitorForm.amenities.slice(0, 8).map((a, i) => (
                          <Chip
                            key={`a-${i}`}
                            label={String(a).replace(/_/g, ' ')}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: isDark ? 'rgba(76, 225, 182, 0.28)' : 'rgba(76, 225, 182, 0.35)',
                              color: isDark ? drawerTheme.text : '#0f172a',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      width: '100%',
                      gap: 1,
                      ...getBookingDrawerFooterSx(isDark),
                    }}
                  >
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={closeAddCompetitorDrawer}
                      disabled={addCompetitorSaving}
                      sx={{ flex: 1, textTransform: 'none', ...getBookingDrawerOutlinedButtonSx(isDark) }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAddCompetitorSave}
                      disabled={addCompetitorSaving}
                      sx={{ flex: 1, textTransform: 'none', ...getBookingDrawerPrimaryButtonSx(isDark) }}
                    >
                      {addCompetitorSaving ? 'Adding…' : 'Add Competitor'}
                    </Button>
                  </Box>
                </Box>
              </Drawer>
            </Box>
          )}

          {activeTab === 5 && (
            <Box>
              <DynamicPricingTabHeader
                title="Pricing simulation"
                description="Run safe what-if scenarios over your dates — no rates are published to PMS or channels."
              />
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={getDynamicPricingGuidePanelSx(isDark, ACCENT_COLOR)}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <Avatar sx={getDynamicPricingGuideAvatarSx(isDark, ACCENT_COLOR)}>
                      <Science sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                        Simulate guide
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Safe preview — no rates published
                      </Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ mb: 2, borderColor: isDark ? 'rgba(255,255,255,0.08)' : undefined }} />
                  <List dense disablePadding sx={{ '& .MuiListItem-root': { alignItems: 'flex-start', py: 1.25 } }}>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40, mt: 0.25, color: ACCENT_COLOR }}>
                        <Science fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="subtitle2" fontWeight={600}>What simulation does</Typography>}
                        secondary="Runs the pricing model twice over your dates: once as baseline, once with your weekend uplift and last-minute discount. You get revenue estimates only—no PMS or channel updates."
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div', variant: 'body2', sx: { mt: 0.5, color: 'text.secondary', lineHeight: 1.55 } }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40, mt: 0.25, color: ACCENT_COLOR }}>
                        <Event fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="subtitle2" fontWeight={600}>Date range</Typography>}
                        secondary="Choose the stay dates you care about (e.g. next month or an event week). Wider ranges take longer but show total modeled impact."
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div', variant: 'body2', sx: { mt: 0.5, color: 'text.secondary', lineHeight: 1.55 } }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40, mt: 0.25, color: ACCENT_COLOR }}>
                        <TrendingUp fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="subtitle2" fontWeight={600}>Weekend markup</Typography>}
                        secondary="Adds a percentage on weekend nights in the scenario run only. Use it to test stronger Fri–Sat pricing without changing Config yet."
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div', variant: 'body2', sx: { mt: 0.5, color: 'text.secondary', lineHeight: 1.55 } }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40, mt: 0.25, color: ACCENT_COLOR }}>
                        <ScheduleOutlined fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="subtitle2" fontWeight={600}>Last-minute discount</Typography>}
                        secondary="Applies a discount for bookings inside the server’s last-minute window (3 days). Adjust strength with the slider; the window length is fixed for now."
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div', variant: 'body2', sx: { mt: 0.5, color: 'text.secondary', lineHeight: 1.55 } }}
                      />
                    </ListItem>
                  </List>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <InfoOutlined sx={{ fontSize: 20, color: 'text.secondary', mt: 0.15 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      Hover (?) next to each control for details. After tuning <strong>Config</strong>, run Simulate here, then review <strong>Recommendations</strong> before applying rates.
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                  <Paper elevation={0} sx={getDynamicPricingPanelSx(isDark, ACCENT_COLOR)}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2 }}>
                      <Typography
                        variant="overline"
                        sx={getDynamicPricingSectionOverlineSx(ACCENT_COLOR)}
                      >
                        Scenario
                      </Typography>
                      <ConfigHelpTip title={PRICING_SIMULATE_HELP.scenarioSection} ariaLabel="About simulation scenario" />
                    </Stack>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Start date"
                          type="date"
                          value={simStart}
                          onChange={(e) => setSimStart(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <ConfigHelpTip title={PRICING_SIMULATE_HELP.simStart} ariaLabel="About simulation start date" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="End date"
                          type="date"
                          value={simEnd}
                          onChange={(e) => setSimEnd(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <ConfigHelpTip title={PRICING_SIMULATE_HELP.simEnd} ariaLabel="About simulation end date" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Stack direction="row" alignItems="center" spacing={0.25}>
                            <Typography variant="body2" fontWeight={600}>
                              Weekend markup %
                            </Typography>
                            <ConfigHelpTip title={PRICING_SIMULATE_HELP.weekendMarkup} ariaLabel="About weekend markup" />
                          </Stack>
                          <Chip
                            size="small"
                            label={`${simWeekendMarkup}%`}
                            sx={{ bgcolor: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.15)', fontWeight: 700 }}
                          />
                        </Stack>
                        <Slider
                          value={simWeekendMarkup}
                          onChange={(_, v) => setSimWeekendMarkup(v)}
                          min={0}
                          max={50}
                          valueLabelDisplay="auto"
                          sx={{ color: ACCENT_COLOR }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Stack direction="row" alignItems="center" spacing={0.25}>
                            <Typography variant="body2" fontWeight={600}>
                              Last-minute discount %
                            </Typography>
                            <ConfigHelpTip
                              title={`${PRICING_SIMULATE_HELP.lastMinuteDiscount} ${PRICING_SIMULATE_HELP.lastMinuteWindowNote}`}
                              ariaLabel="About last-minute discount"
                            />
                          </Stack>
                          <Chip
                            size="small"
                            label={`${simLastMinute}%`}
                            sx={{ bgcolor: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.15)', fontWeight: 700 }}
                          />
                        </Stack>
                        <Slider
                          value={simLastMinute}
                          onChange={(_, v) => setSimLastMinute(v)}
                          min={0}
                          max={30}
                          valueLabelDisplay="auto"
                          sx={{ color: ACCENT_COLOR }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                          <Tooltip
                            title={
                              <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 280, lineHeight: 1.45 }}>
                                {PRICING_SIMULATE_HELP.runButton}
                              </Typography>
                            }
                            arrow
                            enterTouchDelay={0}
                            componentsProps={{
                              tooltip: { sx: { bgcolor: 'grey.900', maxWidth: 320, '& .MuiTooltip-arrow': { color: 'grey.900' } } },
                            }}
                          >
                            <span>
                              <Button
                                variant="contained"
                                startIcon={<Science />}
                                onClick={runSimulation}
                                disabled={loading}
                                sx={{
                                  bgcolor: ACCENT_COLOR,
                                  color: '#0d1f1a',
                                  fontWeight: 700,
                                  px: 2.5,
                                  boxShadow: '0 8px 20px rgba(76, 225, 182, 0.35)',
                                  '&:hover': { bgcolor: '#3dd9a8' },
                                }}
                              >
                                Run simulation
                              </Button>
                            </span>
                          </Tooltip>
                          {loading && <CircularProgress size={28} sx={{ color: ACCENT_COLOR }} />}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                  {simResult && !loading && (
                    <Paper elevation={0} sx={getDynamicPricingPanelSx(isDark, ACCENT_COLOR)}>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Results
                        </Typography>
                        <ConfigHelpTip title={PRICING_SIMULATE_HELP.resultsOverview} ariaLabel="About simulation results" />
                      </Stack>
                      <Stack spacing={1.5}>
                        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                          <Typography variant="body2" color="text.secondary">
                            Baseline revenue:
                          </Typography>
                          <Typography variant="body1" fontWeight={600} component="span">
                            {simResult.estimated_revenue_baseline != null
                              ? Number(simResult.estimated_revenue_baseline).toFixed(2)
                              : '—'}
                          </Typography>
                          <ConfigHelpTip title={PRICING_SIMULATE_HELP.baselineLine} ariaLabel="About baseline revenue" />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                          <Typography variant="body2" color="text.secondary">
                            Scenario revenue:
                          </Typography>
                          <Typography variant="body1" fontWeight={600} component="span">
                            {simResult.estimated_revenue_scenario != null
                              ? Number(simResult.estimated_revenue_scenario).toFixed(2)
                              : '—'}
                          </Typography>
                          <ConfigHelpTip title={PRICING_SIMULATE_HELP.scenarioLine} ariaLabel="About scenario revenue" />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                          <Typography variant="body2" color="text.secondary">
                            Delta:
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight={700}
                            component="span"
                            sx={{ color: ACCENT_COLOR }}
                          >
                            {simResult.estimated_revenue_delta != null
                              ? Number(simResult.estimated_revenue_delta).toFixed(2)
                              : '—'}
                          </Typography>
                          <ConfigHelpTip title={PRICING_SIMULATE_HELP.deltaLine} ariaLabel="About revenue delta" />
                        </Stack>
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              </Grid>
            </Grid>
            </Box>
          )}

          {activeTab === 6 && (
            <Box>
              <DynamicPricingTabHeader
                title="Demand forecast"
                description="Project occupancy and rate trends using your configured signals and historical pickup."
              />
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <Paper elevation={0} sx={getDynamicPricingGuidePanelSx(isDark, ACCENT_COLOR)}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <Avatar sx={getDynamicPricingGuideAvatarSx(isDark, ACCENT_COLOR)}>
                      <ShowChart sx={{ fontSize: 24 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                        Forecast guide
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Demand occupancy by night
                      </Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ mb: 2, borderColor: isDark ? 'rgba(255,255,255,0.08)' : undefined }} />
                  <List dense disablePadding sx={{ '& .MuiListItem-root': { alignItems: 'flex-start', py: 1.25 } }}>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40, mt: 0.25, color: ACCENT_COLOR }}>
                        <ShowChart fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="subtitle2" fontWeight={600}>What you see</Typography>}
                        secondary={PRICING_FORECAST_HELP.overview}
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div', variant: 'body2', sx: { mt: 0.5, color: 'text.secondary', lineHeight: 1.55 } }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40, mt: 0.25, color: ACCENT_COLOR }}>
                        <Apartment fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="subtitle2" fontWeight={600}>Room type scope</Typography>}
                        secondary={PRICING_FORECAST_HELP.roomType}
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div', variant: 'body2', sx: { mt: 0.5, color: 'text.secondary', lineHeight: 1.55 } }}
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 40, mt: 0.25, color: ACCENT_COLOR }}>
                        <Event fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography variant="subtitle2" fontWeight={600}>Date range</Typography>}
                        secondary="Keep the window focused; very long ranges are harder to read and are capped at 366 nights per request."
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div', variant: 'body2', sx: { mt: 0.5, color: 'text.secondary', lineHeight: 1.55 } }}
                      />
                    </ListItem>
                  </List>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <InfoOutlined sx={{ fontSize: 20, color: 'text.secondary', mt: 0.15 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      Turn on <strong>use forecast</strong> under Config so recommendations can consume these signals. Hover (?) next to each control for field-level help.
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                  <Paper elevation={0} sx={getDynamicPricingPanelSx(isDark, ACCENT_COLOR)}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2 }}>
                      <Typography
                        variant="overline"
                        sx={getDynamicPricingSectionOverlineSx(ACCENT_COLOR)}
                      >
                        Forecast range
                      </Typography>
                      <ConfigHelpTip title={PRICING_FORECAST_HELP.overview} ariaLabel="About demand forecast" />
                    </Stack>
                    {forecastLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
                    {forecastError && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setForecastError(null)}>
                        {forecastError}
                      </Alert>
                    )}
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid item xs={12} sm={6} md={4}>
                        <Stack direction="row" alignItems="center" spacing={0.35}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Start date"
                            type="date"
                            value={forecastStart}
                            onChange={(e) => setForecastStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          <ConfigHelpTip title={PRICING_FORECAST_HELP.startDate} ariaLabel="About forecast start date" />
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Stack direction="row" alignItems="center" spacing={0.35}>
                          <TextField
                            fullWidth
                            size="small"
                            label="End date"
                            type="date"
                            value={forecastEnd}
                            onChange={(e) => setForecastEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          <ConfigHelpTip title={PRICING_FORECAST_HELP.endDate} ariaLabel="About forecast end date" />
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Stack direction="row" alignItems="center" spacing={0.35}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="forecast-room-type-label">Room type</InputLabel>
                            <Select
                              labelId="forecast-room-type-label"
                              label="Room type"
                              value={forecastRoomTypeId}
                              onChange={(e) => setForecastRoomTypeId(e.target.value)}
                            >
                              <MenuItem value="">
                                <em>All (property-wide)</em>
                              </MenuItem>
                              {roomTypes.map((rt) => (
                                <MenuItem key={rt.id} value={String(rt.id)}>
                                  {rt.name || rt.code || `Room type ${rt.id}`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <ConfigHelpTip title={PRICING_FORECAST_HELP.roomType} ariaLabel="About room type scope" />
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Button
                            variant="contained"
                            startIcon={forecastLoading ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
                            onClick={() => void fetchForecast()}
                            disabled={forecastLoading}
                            sx={{ bgcolor: ACCENT_COLOR, color: '#000', fontWeight: 600, '&:hover': { bgcolor: '#3bc9a8' } }}
                          >
                            {forecastLoading ? 'Loading…' : 'Load forecast'}
                          </Button>
                          <ConfigHelpTip title={PRICING_FORECAST_HELP.loadButton} ariaLabel="About load forecast" />
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            Click after changing dates or room type.
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>

                  {forecasts.length > 0 && (
                    <Paper elevation={0} sx={getDynamicPricingPanelSx(isDark, ACCENT_COLOR)}>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                        <Typography
                          variant="overline"
                          sx={getDynamicPricingSectionOverlineSx(ACCENT_COLOR)}
                        >
                          Occupancy trend
                        </Typography>
                        <ConfigHelpTip title={PRICING_FORECAST_HELP.chart} ariaLabel="About forecast chart" />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {forecastEstimatedCount > 0
                          ? `${forecastEstimatedCount} of ${forecasts.length} night${forecasts.length === 1 ? '' : 's'} use weekday/weekend estimates (no saved forecast for that scope).`
                          : 'All values in this range come from stored forecasts.'}
                      </Typography>
                      <Box sx={{ width: '100%', height: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={forecastChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#3a3a44' : '#e8e8ed'} />
                            <XAxis dataKey="shortDate" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={28} />
                            <YAxis
                              domain={[0, 'auto']}
                              tickFormatter={(v) => `${v}%`}
                              width={44}
                              tick={{ fontSize: 11 }}
                            />
                            <RcTooltip content={<ForecastChartTooltip />} />
                            <Line
                              type="monotone"
                              dataKey="occupancy_pct"
                              stroke={ACCENT_COLOR}
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 5, stroke: ACCENT_COLOR, fill: isDark ? '#1e1e24' : '#fff' }}
                              connectNulls
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </Paper>
                  )}

                  {forecasts.length > 0 && (
                    <Paper elevation={0} sx={getDynamicPricingPanelSx(isDark, ACCENT_COLOR)}>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 2 }}>
                        <Typography
                          variant="overline"
                          sx={getDynamicPricingSectionOverlineSx(ACCENT_COLOR)}
                        >
                          Daily detail
                        </Typography>
                        <ConfigHelpTip title={PRICING_FORECAST_HELP.table} ariaLabel="About forecast table" />
                      </Stack>
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ maxHeight: 380, borderColor: 'divider' }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell align="right">Occupancy %</TableCell>
                              <TableCell>Source</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {forecasts.map((f) => (
                              <TableRow key={f.date} hover>
                                <TableCell>{moment(f.date).format('ddd, MMM D, YYYY')}</TableCell>
                                <TableCell align="right">
                                  {Number(f.occupancy_percent).toLocaleString(undefined, {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2,
                                  })}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    variant={f.source === 'estimated' ? 'outlined' : 'filled'}
                                    label={forecastSourceLabel(f.source)}
                                    sx={{
                                      height: 22,
                                      fontWeight: 600,
                                      ...(f.source === 'estimated'
                                        ? { borderColor: 'divider' }
                                        : {
                                            bgcolor: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.18)',
                                            color: 'text.primary',
                                          }),
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  )}
                </Stack>
              </Grid>
            </Grid>
            </Box>
          )}

          {activeTab === 7 && (
            <Box>
              <DynamicPricingTabHeader
                title="Pricing configuration"
                description="Tune signal weights, guardrails, and automation — then simulate before publishing rates."
              />
              {config && (
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid item xs={12} md={4}>
                    <Paper elevation={0} sx={getDynamicPricingGuidePanelSx(isDark, ACCENT_COLOR)}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                        <Avatar sx={getDynamicPricingGuideAvatarSx(isDark, ACCENT_COLOR)}>
                          <Settings sx={{ fontSize: 24 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                            Config guide
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            How these settings affect dynamic pricing
                          </Typography>
                        </Box>
                      </Stack>
                      <Divider sx={{ mb: 2, borderColor: isDark ? 'rgba(255,255,255,0.08)' : undefined }} />
                      <List dense disablePadding sx={{ '& .MuiListItem-root': { alignItems: 'flex-start', py: 1.25 } }}>
                        <ListItem disableGutters>
                          <ListItemIcon sx={{ minWidth: 40, mt: 0.25, color: ACCENT_COLOR }}>
                            <Tune fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="subtitle2" fontWeight={600}>Formula weights</Typography>}
                            secondary="Sliders set relative strength (0–100) for occupancy, booking window, booking pace, and competitor rates. They are scaled together—you do not need them to total 100. Hover the (?) icons on the right for each signal’s meaning."
                            primaryTypographyProps={{ component: 'div' }}
                            secondaryTypographyProps={{ component: 'div', variant: 'body2', sx: { mt: 0.5, color: 'text.secondary', lineHeight: 1.55 } }}
                          />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon sx={{ minWidth: 40, mt: 0.25, color: ACCENT_COLOR }}>
                            <CompareArrows fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="subtitle2" fontWeight={600}>Price bounds</Typography>}
                            secondary="Min/max multipliers clamp every suggestion to base rate × factor (e.g. min 0.7, max 2.0). Stops the model from recommending prices far outside your commercial guardrails."
                            primaryTypographyProps={{ component: 'div' }}
                            secondaryTypographyProps={{ component: 'div', variant: 'body2', sx: { mt: 0.5, color: 'text.secondary', lineHeight: 1.55 } }}
                          />
                        </ListItem>
                        <ListItem disableGutters>
                          <ListItemIcon sx={{ minWidth: 40, mt: 0.25, color: ACCENT_COLOR }}>
                            <ShowChart fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="subtitle2" fontWeight={600}>Signals & automation</Typography>}
                            secondary="Each switch fully includes or excludes a data source. Off = that input is not used at all. Cron interval (minutes) schedules recurring pricing jobs; 0 turns that schedule off—your server must still run the worker for jobs to execute."
                            primaryTypographyProps={{ component: 'div' }}
                            secondaryTypographyProps={{ component: 'div', variant: 'body2', sx: { mt: 0.5, color: 'text.secondary', lineHeight: 1.55 } }}
                          />
                        </ListItem>
                      </List>
                      <Divider sx={{ my: 2 }} />
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <InfoOutlined sx={{ fontSize: 20, color: 'text.secondary', mt: 0.15 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                          <strong>Tip:</strong> Change a little, save, then open <strong>Simulate</strong> to see impact before applying rates. Use <strong>Recommendations</strong> to review suggested prices. The (?) icons next to each control explain that field in one place.
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Stack spacing={3}>
                      <Paper elevation={0} sx={getDynamicPricingPanelSx(isDark, ACCENT_COLOR)}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                          <Typography
                            variant="overline"
                            sx={getDynamicPricingSectionOverlineSx(ACCENT_COLOR)}
                          >
                            Rate formula
                          </Typography>
                          <ConfigHelpTip title={PRICING_CONFIG_HELP.rateFormulaCard} ariaLabel="About the rate formula section" />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2.5 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            Weights
                          </Typography>
                          <ConfigHelpTip title={PRICING_CONFIG_HELP.weightsSection} ariaLabel="About formula weights" />
                        </Stack>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Stack direction="row" alignItems="center" spacing={0.25}>
                                <Typography variant="body2" fontWeight={600}>
                                  Occupancy
                                </Typography>
                                <ConfigHelpTip title={PRICING_CONFIG_HELP.occupancyWeight} ariaLabel="About occupancy weight" />
                              </Stack>
                              <Chip size="small" label={`${Number(config.occupancy_weight) || 30}`} sx={{ bgcolor: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.15)', fontWeight: 700 }} />
                            </Stack>
                            <Slider
                              value={Number(config.occupancy_weight) || 30}
                              onChange={(_, v) => setConfig((c) => ({ ...c, occupancy_weight: v }))}
                              min={0}
                              max={100}
                              valueLabelDisplay="auto"
                              sx={{ color: ACCENT_COLOR }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Stack direction="row" alignItems="center" spacing={0.25}>
                                <Typography variant="body2" fontWeight={600}>
                                  Lead time
                                </Typography>
                                <ConfigHelpTip title={PRICING_CONFIG_HELP.leadTimeWeight} ariaLabel="About lead time weight" />
                              </Stack>
                              <Chip size="small" label={`${Number(config.lead_time_weight) || 25}`} sx={{ bgcolor: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.15)', fontWeight: 700 }} />
                            </Stack>
                            <Slider
                              value={Number(config.lead_time_weight) || 25}
                              onChange={(_, v) => setConfig((c) => ({ ...c, lead_time_weight: v }))}
                              min={0}
                              max={100}
                              valueLabelDisplay="auto"
                              sx={{ color: ACCENT_COLOR }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Stack direction="row" alignItems="center" spacing={0.25}>
                                <Typography variant="body2" fontWeight={600}>
                                  Velocity
                                </Typography>
                                <ConfigHelpTip title={PRICING_CONFIG_HELP.velocityWeight} ariaLabel="About velocity weight" />
                              </Stack>
                              <Chip size="small" label={`${Number(config.velocity_weight) || 25}`} sx={{ bgcolor: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.15)', fontWeight: 700 }} />
                            </Stack>
                            <Slider
                              value={Number(config.velocity_weight) || 25}
                              onChange={(_, v) => setConfig((c) => ({ ...c, velocity_weight: v }))}
                              min={0}
                              max={100}
                              valueLabelDisplay="auto"
                              sx={{ color: ACCENT_COLOR }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                              <Stack direction="row" alignItems="center" spacing={0.25}>
                                <Typography variant="body2" fontWeight={600}>
                                  Competitor
                                </Typography>
                                <ConfigHelpTip title={PRICING_CONFIG_HELP.competitorWeight} ariaLabel="About competitor weight" />
                              </Stack>
                              <Chip size="small" label={`${Number(config.competitor_weight) || 20}`} sx={{ bgcolor: isDark ? 'rgba(76,225,182,0.12)' : 'rgba(76,225,182,0.15)', fontWeight: 700 }} />
                            </Stack>
                            <Slider
                              value={Number(config.competitor_weight) || 20}
                              onChange={(_, v) => setConfig((c) => ({ ...c, competitor_weight: v }))}
                              min={0}
                              max={100}
                              valueLabelDisplay="auto"
                              sx={{ color: ACCENT_COLOR }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                      <Paper elevation={0} sx={getDynamicPricingPanelSx(isDark, ACCENT_COLOR)}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                          <Typography
                            variant="overline"
                            sx={getDynamicPricingSectionOverlineSx(ACCENT_COLOR)}
                          >
                            Guardrails
                          </Typography>
                          <ConfigHelpTip
                            title="Safety limits on the final suggestion: after the formula runs, the rate is clipped so it never falls below base × min or above base × max."
                            ariaLabel="About guardrails"
                          />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            Default price bounds
                          </Typography>
                          <ConfigHelpTip title={PRICING_CONFIG_HELP.boundsSection} ariaLabel="About price bounds" />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Base rate × multiplier — floor and ceiling for recommendations.
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Min multiplier"
                              type="number"
                              inputProps={{ min: 0.01, max: 2, step: 0.01 }}
                              value={config.default_min_multiplier ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setConfig((c) => ({
                                  ...c,
                                  default_min_multiplier: v === '' ? '' : Number(v),
                                }));
                              }}
                              helperText="e.g. 0.5 → floor ≈ half of base"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <ConfigHelpTip title={PRICING_CONFIG_HELP.minMultiplier} ariaLabel="About min multiplier" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Max multiplier"
                              type="number"
                              inputProps={{ min: 1, max: 5, step: 0.01 }}
                              value={config.default_max_multiplier ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setConfig((c) => ({
                                  ...c,
                                  default_max_multiplier: v === '' ? '' : Number(v),
                                }));
                              }}
                              helperText="e.g. 2 → cap ≈ double base"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <ConfigHelpTip title={PRICING_CONFIG_HELP.maxMultiplier} ariaLabel="About max multiplier" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                      <Paper elevation={0} sx={getDynamicPricingPanelSx(isDark, ACCENT_COLOR)}>
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                          <Typography
                            variant="overline"
                            sx={getDynamicPricingSectionOverlineSx(ACCENT_COLOR)}
                          >
                            Inputs
                          </Typography>
                          <ConfigHelpTip
                            title="Choose which data feeds the calculator. Switches are stronger than low weights: when off, that signal is omitted entirely."
                            ariaLabel="About inputs section"
                          />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            Signals
                          </Typography>
                          <ConfigHelpTip title={PRICING_CONFIG_HELP.signalsSection} ariaLabel="About signals" />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          Hover a tile for what each switch does. Off = that input is not used.
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <Tooltip
                              title={
                                <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 280, lineHeight: 1.45 }}>
                                  {PRICING_CONFIG_HELP.useOccupancy}
                                </Typography>
                              }
                              placement="top"
                              arrow
                              enterTouchDelay={0}
                              componentsProps={{ tooltip: { sx: { bgcolor: 'grey.900', maxWidth: 320, '& .MuiTooltip-arrow': { color: 'grey.900' } } } }}
                            >
                              <Paper elevation={0} sx={getDynamicPricingConfigTogglePaperSx(isDark, ACCENT_COLOR)}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={!!config.use_occupancy}
                                      onChange={(e) => setConfig((c) => ({ ...c, use_occupancy: e.target.checked }))}
                                      sx={getDynamicPricingSwitchSx(ACCENT_COLOR)}
                                    />
                                  }
                                  label={<Typography variant="body2">Occupancy</Typography>}
                                />
                              </Paper>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Tooltip
                              title={
                                <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 280, lineHeight: 1.45 }}>
                                  {PRICING_CONFIG_HELP.useLeadTime}
                                </Typography>
                              }
                              placement="top"
                              arrow
                              enterTouchDelay={0}
                              componentsProps={{ tooltip: { sx: { bgcolor: 'grey.900', maxWidth: 320, '& .MuiTooltip-arrow': { color: 'grey.900' } } } }}
                            >
                              <Paper elevation={0} sx={getDynamicPricingConfigTogglePaperSx(isDark, ACCENT_COLOR)}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={!!config.use_lead_time}
                                      onChange={(e) => setConfig((c) => ({ ...c, use_lead_time: e.target.checked }))}
                                      sx={getDynamicPricingSwitchSx(ACCENT_COLOR)}
                                    />
                                  }
                                  label={<Typography variant="body2">Lead time</Typography>}
                                />
                              </Paper>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Tooltip
                              title={
                                <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 280, lineHeight: 1.45 }}>
                                  {PRICING_CONFIG_HELP.useVelocity}
                                </Typography>
                              }
                              placement="top"
                              arrow
                              enterTouchDelay={0}
                              componentsProps={{ tooltip: { sx: { bgcolor: 'grey.900', maxWidth: 320, '& .MuiTooltip-arrow': { color: 'grey.900' } } } }}
                            >
                              <Paper elevation={0} sx={getDynamicPricingConfigTogglePaperSx(isDark, ACCENT_COLOR)}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={!!config.use_velocity}
                                      onChange={(e) => setConfig((c) => ({ ...c, use_velocity: e.target.checked }))}
                                      sx={getDynamicPricingSwitchSx(ACCENT_COLOR)}
                                    />
                                  }
                                  label={<Typography variant="body2">Velocity</Typography>}
                                />
                              </Paper>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Tooltip
                              title={
                                <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 280, lineHeight: 1.45 }}>
                                  {PRICING_CONFIG_HELP.useCompetitor}
                                </Typography>
                              }
                              placement="top"
                              arrow
                              enterTouchDelay={0}
                              componentsProps={{ tooltip: { sx: { bgcolor: 'grey.900', maxWidth: 320, '& .MuiTooltip-arrow': { color: 'grey.900' } } } }}
                            >
                              <Paper elevation={0} sx={getDynamicPricingConfigTogglePaperSx(isDark, ACCENT_COLOR)}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={!!config.use_competitor}
                                      onChange={(e) => setConfig((c) => ({ ...c, use_competitor: e.target.checked }))}
                                      sx={getDynamicPricingSwitchSx(ACCENT_COLOR)}
                                    />
                                  }
                                  label={<Typography variant="body2">Competitor</Typography>}
                                />
                              </Paper>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Tooltip
                              title={
                                <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 280, lineHeight: 1.45 }}>
                                  {PRICING_CONFIG_HELP.useForecast}
                                </Typography>
                              }
                              placement="top"
                              arrow
                              enterTouchDelay={0}
                              componentsProps={{ tooltip: { sx: { bgcolor: 'grey.900', maxWidth: 320, '& .MuiTooltip-arrow': { color: 'grey.900' } } } }}
                            >
                              <Paper elevation={0} sx={getDynamicPricingConfigTogglePaperSx(isDark, ACCENT_COLOR)}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={!!config.use_forecast}
                                      onChange={(e) => setConfig((c) => ({ ...c, use_forecast: e.target.checked }))}
                                      sx={getDynamicPricingSwitchSx(ACCENT_COLOR)}
                                    />
                                  }
                                  label={<Typography variant="body2">Forecast</Typography>}
                                />
                              </Paper>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Tooltip
                              title={
                                <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 280, lineHeight: 1.45 }}>
                                  {PRICING_CONFIG_HELP.useEvents}
                                </Typography>
                              }
                              placement="top"
                              arrow
                              enterTouchDelay={0}
                              componentsProps={{ tooltip: { sx: { bgcolor: 'grey.900', maxWidth: 320, '& .MuiTooltip-arrow': { color: 'grey.900' } } } }}
                            >
                              <Paper elevation={0} sx={getDynamicPricingConfigTogglePaperSx(isDark, ACCENT_COLOR)}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={!!config.use_events}
                                      onChange={(e) => setConfig((c) => ({ ...c, use_events: e.target.checked }))}
                                      sx={getDynamicPricingSwitchSx(ACCENT_COLOR)}
                                    />
                                  }
                                  label={<Typography variant="body2">Events</Typography>}
                                />
                              </Paper>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mt: 1 }}>
                              <ScheduleOutlined sx={{ color: 'text.secondary', mt: 0.5 }} />
                              <TextField
                                fullWidth
                                size="small"
                                label="Cron interval (minutes)"
                                type="number"
                                inputProps={{ min: 0, step: 1 }}
                                value={config.cron_interval_minutes ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setConfig((c) => ({
                                    ...c,
                                    cron_interval_minutes: v === '' ? null : Number(v),
                                  }));
                                }}
                                helperText="0 = disabled"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <ConfigHelpTip title={PRICING_CONFIG_HELP.cronInterval} ariaLabel="About cron interval" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Stack>
                          </Grid>
                        </Grid>
                      </Paper>
                      <Paper
                        elevation={0}
                        sx={{
                          ...getDynamicPricingPanelSx(isDark, ACCENT_COLOR, { p: 2.5 }),
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ flex: '1 1 220px', mr: 'auto' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                            Changes apply to the next recommendation and automation runs.
                          </Typography>
                          <ConfigHelpTip title={PRICING_CONFIG_HELP.saveButton} ariaLabel="About saving configuration" />
                        </Stack>
                        <Tooltip
                          title={
                            <Typography variant="body2" component="span" sx={{ display: 'block', maxWidth: 280, lineHeight: 1.45 }}>
                              {PRICING_CONFIG_HELP.saveButton}
                            </Typography>
                          }
                          arrow
                          placement="top"
                          enterTouchDelay={0}
                          componentsProps={{ tooltip: { sx: { bgcolor: 'grey.900', maxWidth: 320, '& .MuiTooltip-arrow': { color: 'grey.900' } } } }}
                        >
                          <span>
                            <Button
                              variant="contained"
                              size="large"
                              onClick={handleSaveConfig}
                              disabled={loading}
                              sx={{
                                bgcolor: ACCENT_COLOR,
                                color: '#0d1f1a',
                                fontWeight: 700,
                                px: 3,
                                boxShadow: '0 8px 20px rgba(76, 225, 182, 0.35)',
                                '&:hover': { bgcolor: '#3dd9a8', boxShadow: '0 10px 24px rgba(76, 225, 182, 0.45)' },
                              }}
                            >
                              Save configuration
                            </Button>
                          </span>
                        </Tooltip>
                      </Paper>
                    </Stack>
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {activeTab === 8 && (
            <Box>
              <DynamicPricingTabHeader
                title="History & logs"
                description="Audit publish batches and recent rate changes across room types and plans."
              />
              <Tabs
                value={historyLogsTab}
                onChange={handleHistoryLogsTabChange}
                variant="standard"
                sx={{ mb: 2, ...getDynamicPricingAccentTabsSx(ACCENT_COLOR) }}
              >
                <Tab label="Publish logs" id="history-tab-publish" aria-controls="history-panel-publish" />
                <Tab label="History (recent)" id="history-tab-pricing" aria-controls="history-panel-pricing" />
              </Tabs>

              {historyLogsTab === 0 && (
                <Box>
                  {publishLogsLoading && publishLogs.length === 0 ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={36} sx={{ color: ACCENT_COLOR }} />
                    </Box>
                  ) : (
                    <>
                      <TableContainer
                        ref={publishLogsScrollBoxRef}
                        component={Paper}
                        elevation={0}
                        onScroll={handlePublishLogsScroll}
                        sx={getDynamicPricingTableContainerSx(isDark, ACCENT_COLOR)}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Time</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Success</TableCell>
                              <TableCell align="right">Failure</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {publishLogs.length === 0 && !publishLogsLoading ? (
                              <TableRow>
                                <TableCell colSpan={4}>
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                    No publish logs yet. PMS publish runs will appear here.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              publishLogs.map((log) => (
                                <TableRow key={log.id} hover>
                                  <TableCell>{log.created_at}</TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      color={
                                        log.status === 'SUCCESS' ? 'success' : log.status === 'FAILED' ? 'error' : 'default'
                                      }
                                      label={log.status}
                                    />
                                  </TableCell>
                                  <TableCell align="right">{log.success_count}</TableCell>
                                  <TableCell align="right">{log.failure_count}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {publishLogsLoading && publishLogs.length > 0 && (
                        <LinearProgress sx={{ mt: 0.5, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: ACCENT_COLOR } }} />
                      )}
                      {publishLogs.length > 0 && !publishLogsHasMore && !publishLogsLoading && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                          End of publish logs
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}

              {historyLogsTab === 1 && (
                <Box>
                  {historyLoading && history.length === 0 ? (
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={36} sx={{ color: ACCENT_COLOR }} />
                    </Box>
                  ) : (
                    <>
                      <TableContainer
                        ref={historyScrollBoxRef}
                        component={Paper}
                        elevation={0}
                        onScroll={handleHistoryScroll}
                        sx={getDynamicPricingTableContainerSx(isDark, ACCENT_COLOR)}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>When</TableCell>
                              <TableCell>Room type</TableCell>
                              <TableCell>Plan</TableCell>
                              <TableCell>Date</TableCell>
                              <TableCell align="right">Previous</TableCell>
                              <TableCell align="right">New rate</TableCell>
                              <TableCell>Source</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {history.length === 0 && !historyLoading ? (
                              <TableRow>
                                <TableCell colSpan={7}>
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                    No rate changes recorded yet.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              history.map((h) => (
                                <TableRow key={h.id} hover>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{h.created_at ?? '—'}</TableCell>
                                  <TableCell>{h.room_type_id}</TableCell>
                                  <TableCell>{h.rate_plan_id}</TableCell>
                                  <TableCell>{h.rate_date}</TableCell>
                                  <TableCell align="right">{h.previous_rate ?? '—'}</TableCell>
                                  <TableCell align="right">{h.new_rate}</TableCell>
                                  <TableCell>{h.source}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {historyLoading && history.length > 0 && (
                        <LinearProgress sx={{ mt: 0.5, borderRadius: 1, '& .MuiLinearProgress-bar': { bgcolor: ACCENT_COLOR } }} />
                      )}
                      {history.length > 0 && !historyHasMore && !historyLoading && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                          End of history
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}

      </Paper>

      <CompetitorSyncProgressDialog
        open={Boolean(syncProgressDialog?.open)}
        title={syncProgressDialog?.title}
        steps={syncProgressDialog?.steps ?? []}
        footerNote={syncProgressDialog?.footerNote}
        errorMessage={syncProgressDialog?.errorMessage}
        completed={Boolean(syncProgressDialog?.completed)}
        isDark={isDark}
        onClose={closeSyncProgressDialog}
        itemProgress={syncProgressDialog?.itemProgress ?? null}
      />
      <PageToastStack toasts={pageToasts} onDismiss={dismissPageToast} />
    </Box>
  );
};

export default DynamicPricingPage;
